import { aabb, ancestorsOf, createSceneReader, dropPlan, imageAspect, isEffectivelyLocked, flowchartTemplate, newTable, newTaskList, planSectionArrangement, SHAPES, styleOf, topLevel, type FlowchartLabels, type FlowchartTemplate, type NewObject, type ObjectData, type SceneStore, type StyleClip } from '@os/doc';
import type { TaskEditTarget } from '../tasks/task-edit';
import {
  arrangeLayout,
  comboOf,
  cropFrom,
  FULL_CROP,
  fullImageBox,
  handleAt,
  connectorPath,
  createBoxTool,
  createConnectorTool,
  sideOf,
  createSelectTool,
  fitClear,
  fitRect,
  insetsOf,
  handleWorldPos,
  handlesFor,
  HANDLE_IDS,
  hitTest,
  selectionTarget,
  lookupShortcut,
  cursorFor,
  anchorDots,
  neighborSlot,
  plusButtons,
  normalizeAngle,
  NO_HOVER,
  obbBounds,
  obbCorners,
  panBy,
  PinchTracker,
  screenToWorld,
  unionRects,
  visibleRect,
  wheelToIntent,
  worldToScreen,
  keyboardOrder,
  zoomAt,
  snapToGrid,
  type Box,
  type Camera,
  type ArrangeMode,
  type Crop,
  type HandleId,
  type CommandId,
  type EndInput,
  type Guide,
  type Hover,
  type Intent,
  type KeyInput,
  type OBB,
  type Point,
  type PointerInput,
  type PointerType,
  type Rect,
  type SceneReader,
  type Side,
  type Tool,
  type ToolId,
} from '@os/engine';
import { writeFrame, type Frame } from '../objects/frame';
import type { EmbedControl } from './embeds';
import { linkOrEmbedFor } from '../links/create';
import { lastCopied, pasteOffset, payloadBounds } from '../clipboard/clipboard';
import { DEFAULT_STICKY } from '../../ui/palette';
import { motionOff } from '../../ui/motion';
import { SECTION_COLORS, sectionAccent } from '../../ui/section-colors';
import { safeHref } from '../text/sanitize';
import type { MenuAction } from './menu-items';
import { backgroundCss, canvasColors } from './background';
import { chooseVariant, type MediaResolver, type Variant } from './lod';
import { drawOverlay, EMPTY_OVERLAY, type OverlayModel } from './Overlay';
import { isCanvasUi } from './canvas-ui';
import { EDGE_GUTTER_PX } from '../../app/layout';
import { revealAboveKeyboard } from './keyboard-inset';

export type Clock = { now(): number; raf(cb: (t: number) => void): number; caf(handle: number): void };
/** `multiSelect`: touch mode where a tap adds or removes an object (touch has no shift key). */
export type UiState = { tool: ToolId; selection: readonly string[]; readOnly: boolean; canComment: boolean; multiSelect: boolean };
export type CommentRequest = { at: Point; objectId: string | null; screen: Point };
/** `task`: the task under the pointer when the menu opens on a task list (P16-5). */
export type ContextMenuRequest = { x: number; y: number; cell: { rowId: string; colId: string } | null; task?: string | null };
export type Notice = 'styleCopied' | 'stylePasted' | 'locked' | 'nothingToPaste';
/** An object reached with Tab: its type, its text (or section name) and its rank in the reading order (1-based). */
export type FocusInfo = { id: string; type: string; text: string; index: number; total: number };
export type EditorHandlers = {
  onPickImage?(): void;
  onHelp?(): void;
  /** ⌘K: the command palette (P11). */
  onPalette?(): void;
  /** Alt+PageDown / Alt+PageUp (P14): the next or previous page of the board. */
  onPage?(dir: 1 | -1): void;
  /** Double-click or Enter on a page link (P14): open the page it shows. Readers too. */
  onOpenPageLink?(id: string): void;
  onEditText?(id: string): void;
  /** A connector released in the void from an object: offer a linked object there (world point and viewport point). */
  onQuickCreate?(req: { at: Point; screen: Point }): void;
  /** Feedback for an action that would otherwise be silent (a toast). */
  onNotice?(key: Notice): void;
  /** Double-click on a connector: edit the text on its line (never on a read-only board). */
  onEditConnectorLabel?(id: string): void;
  /** Double-click on a section's title (never on a read-only board). */
  onEditSectionTitle?(id: string): void;
  /** Double-click on a table cell, or Enter on a selected table (its first cell); never on a read-only board or a locked table. */
  onEditTableCell?(tableId: string, rowId: string, colId: string): void;
  /** Double-click on a task list's title or task, Enter on a selected list, or a list just created; never read-only or locked. */
  onEditTask?(listId: string, target: TaskEditTarget): void;
  onArrange?(): void;
  /** Right click or long press: client coordinates, and the table cell under the pointer if any. */
  onContextMenu?(req: ContextMenuRequest): void;
  /** A click with the comment tool: world point, topmost object (locked included) and viewport point. */
  onComment?(req: CommentRequest): void;
  /** Tab moved the selection: for the screen reader's announcement (P11). */
  onFocusObject?(info: FocusInfo): void;
};

export type ControllerOptions = {
  store: SceneStore;
  media: MediaResolver;
  clock?: Clock;
  /** Defaults to the viewport's client size (tests inject it: happy-dom has no layout). */
  viewportSize?: () => { w: number; h: number };
  dpr?: () => number;
  /** Resolves once the image at `url` is decoded, so a LOD swap never shows a blank frame. */
  decode?: (url: string) => Promise<void>;
  lodCap?: Variant;
  /** Defaults to fitting the content at attach time. */
  initialCamera?: Camera;
  /**
   * A touch screen (mobile): the board opens in the hand tool, a finger always pans, and an object is picked up by
   * pressing and holding it or by a double tap, which switches to select until the selection is empty again.
   */
  browseFirst?: boolean;
};

export type EditorElements = { viewport: HTMLElement; world: HTMLElement; overlay: SVGSVGElement; background?: HTMLElement };

const TOOL_IDS = new Set<string>(['select', 'hand', 'text', 'sticky', 'shape', 'arrow', 'image', 'section', 'frame', 'connector', 'table', 'tasklist', 'comment']);
/** Tools that create by dragging a box (a click gives the default size). */
const BOX_TOOLS = new Set<ToolId>(['section', 'frame', 'shape']);
/** Tools that create their object at the press (a fixed default size); the shape tool drags its box (P5f). */
const CLICK_TOOLS = new Set<ToolId>(['text', 'sticky', 'arrow', 'table', 'tasklist']);
/** Tools whose object Enter places at the view's centre. */
const CREATE_TOOLS = new Set<ToolId>([...CLICK_TOOLS, 'shape']);
/** A dragged shape is never smaller than this, in world units, whatever the zoom. */
const MIN_SHAPE_WORLD = 8;
/** The context bar stays hidden this long after the last camera move (wheel, keys, minimap…), P5f. */
export const BUSY_SETTLE_MS = 200;
/** Sent on the world layer when the zoom changes (P14): what depends on an object's size on screen listens to it. */
export const ZOOM_EVENT = 'os:zoom';
export const READ_ONLY_TOOLS: ReadonlySet<ToolId> = new Set<ToolId>(['select', 'hand']);
/** Beyond this, a press with the comment tool is a drag (nothing happens). */
const COMMENT_CLICK_PX = 4;
const isTool = (a: ToolId | CommandId): a is ToolId => TOOL_IDS.has(a);
const DUPLICATE_OFFSET = { x: 24, y: 24 };
const ZOOM_STEP = 1.25;
const REST_MS = 150;
const PULSE_MS = 700; // a section's confirmation after it receives a dropped object
/** A new object fades in (P15c): how long its element keeps data-born (the CSS animation is --duration-base, 200 ms). */
export const BORN_MS = 320;
/** More objects at once (a big paste, a duplicate of a whole area) appear at once: no fade for a burst. */
export const BORN_MAX = 20;
const SHAKE_MS = 400; // a locked section's padlock, when a gesture on its content cannot do anything
const RELEASE_MARGIN = 2; // images further than two screens away drop their src (spec §D.5)
const SLOW_FRAME_MS = 20; // frames slower than this for SLOW_FOR_MS degrade the rendering (spec §D.5)
const SLOW_FOR_MS = 1000; // will-change removal and LOD swap wait this long after the last camera move or pointer release
const CULL_MARGIN = 0.5;
const LONG_PRESS_MS = 500;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP_PX = 32;
const TAP_SLOP_PX = 8; // a finger that travelled further is a drag, not a tap
const LIVE_WRITE_MS = 66; // gesture writes to the document: 15 Hz (spec §7.5), the DOM still follows every frame

const browserClock: Clock = {
  now: () => performance.now(),
  raf: (cb) => requestAnimationFrame(cb),
  caf: (h) => cancelAnimationFrame(h),
};

function decodeImage(url: string): Promise<void> {
  const probe = new Image();
  probe.src = url;
  return probe.decode();
}

const frameOf = (o: Readonly<ObjectData>): Frame => ({ x: o.x, y: o.y, w: o.w, h: o.h, rotation: o.rotation });
const obbOf = (f: Frame): OBB => ({ cx: f.x + f.w / 2, cy: f.y + f.h / 2, w: f.w, h: f.h, rotation: f.rotation });
const asPointerType = (t: string): PointerType => (t === 'touch' || t === 'pen' ? t : 'mouse');
/** The two `obbCorners` indices of each side (nw, ne, se, sw): a hovered side is drawn along them. */
const EDGE_CORNERS: Record<string, [number, number]> = { n: [0, 1], e: [1, 2], s: [2, 3], w: [3, 0] };

/** An active text editor (or form field), and canvas UI (`CANVAS_UI`), receive their own events (spec §D.2). */
const isEditable = (t: EventTarget | null) =>
  isCanvasUi(t) || (t instanceof HTMLElement && (t.isContentEditable || t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.tagName === 'SELECT'));
type GestureEvent = Event & { scale: number; clientX: number; clientY: number };

/**
 * The editor's hot loop, outside React: camera → one CSS matrix on the world layer, culling by display:none,
 * LOD swaps at rest, transient drag frames written straight to the DOM, overlay drawn imperatively.
 * During a gesture the document is written at most every LIVE_WRITE_MS (15 Hz), then exactly on commit,
 * through the store's commands, as one undo step.
 */
export class EditorController {
  camera: Camera;
  selection: readonly string[] = [];
  /** The group entered by double-click: clicks select inside it until the selection leaves it. */
  entered: string | null = null;
  /** The object Tab last selected (keyboard walk), cleared by any other selection: Tab walks on from it. */
  private tabbedTo: string | null = null;
  /** Last world position of the pointer over the canvas (paste target); null when it left. */
  pointerWorld: Point | null = null;
  /** The image in crop mode (double-click): its draft crop lives in the overlay until Enter. */
  cropping: string | null = null;
  private cropDraft: Crop | null = null;
  private cropHandle: HandleId | null = null;
  private activeEmbed: string | null = null;
  private readonly embedSubs = new Set<() => void>();
  /** The one playing embed: activated by its play button or a double-click, stopped by any navigation. */
  readonly embeds: EmbedControl = {
    active: () => this.activeEmbed,
    subscribe: (cb) => {
      this.embedSubs.add(cb);
      return () => void this.embedSubs.delete(cb);
    },
    activate: (id) => {
      const next = id && this.store.getObject(id)?.type === 'embed' ? id : null;
      if (next === this.activeEmbed) return;
      this.activeEmbed = next;
      this.embedSubs.forEach((cb) => cb());
    },
  };
  toolId: ToolId = 'select';
  handlers: EditorHandlers = {};
  /** Content of a new text object (translated by the UI). */
  defaultText = 'Text';
  /** Drag hint copy (the editor sets the translated one). */
  dropLabels = {
    into: (name: string) => `Into “${name}”`,
    out: (name: string) => `Remove from “${name}”`,
    leaving: (count: number, name: string) => `${count} leaving “${name}”`,
    untitled: 'Untitled section',
  };
  /** Children a section being resized would let go (P5e). */
  private leaving = new Set<string>();
  /** During a move: the sections the selection would land in, and the hint when a parent changes. */
  private dropHint: { targets: string[]; label: { at: Point; text: string } | null } | null = null;
  /** Sections showing their drop confirmation, and when it ends. */
  private pulses = new Map<string, number>();
  /** Objects created here a moment ago, until when their element keeps data-born. */
  private bornUntil = new Map<string, number>();
  private shakes = new Map<Element, number>();
  private discreteDepth = 0;
  /** Largest image variant ever requested; lowered to 1024 when frames are too slow. */
  lodCap: Variant;
  shapeKind: (typeof SHAPES)[number] = 'rectangle';

  private readonly store: SceneStore;
  private readonly media: MediaResolver;
  private readonly clock: Clock;
  private readonly dpr: () => number;
  private readonly decode: (url: string) => Promise<void>;
  private readonly sizeOverride?: () => { w: number; h: number };
  private readonly hasInitialCamera: boolean;

  private dom: EditorElements | null = null;
  private reader: (SceneReader & { destroy(): void }) | null = null;
  private tool: Tool | null = null;
  private selectTool: Tool | null = null;
  private unsubscribeList: (() => void) | null = null;
  private unsubscribeReadOnly: (() => void) | null = null;
  private unsubscribeMeta: (() => void) | null = null;

  private readonly els = new Map<string, HTMLElement>();
  private visible = new Set<string>();
  private near = new Set<string>();
  private readonly transient = new Map<string, Frame>();
  private readonly origins = new Map<string, Readonly<ObjectData>>();
  /** Live gesture writes: last write time and what the document already has (commands are relative). */
  private liveOpen = false;
  private live = { at: 0, dx: 0, dy: 0, deg: 0 };
  private marquee: Rect | null = null;
  /** The box being dragged out with a box tool (P5f), in world units. */
  private ghost: { tool: ToolId; rect: Rect } | null = null;
  /** A gesture or a camera move is under way (P5f): the selection's context bar hides. */
  private busyNow = false;
  private cameraMovedAt = -Infinity;
  private readonly busySubs = new Set<() => void>();
  /** Connector being drawn or re-attached: world polyline shown in the overlay. */
  private linkPreview: Point[] | null = null;
  /** The end a connector being drawn would attach to (target highlight). */
  private linkTarget: EndInput | null = null;
  /** A connector waiting in the void for the quick-create menu's answer. */
  private quick: { from: EndInput; at: Point } | null = null;
  /** A connector's middle handle being dragged: its writes form one undo step, throttled like other gestures. */
  private bending: { at: number } | null = null;
  /** Alt-drag: originals → their copies, made at the drag's first move inside the live gesture. */
  private copies: { originals: string[]; ids: string[] } | null = null;
  /** The style copied with copy style (in memory: it never leaves this board's editor). */
  private styleClip: StyleClip | null = null;
  /** A text, sticky or shape is being edited in place: no anchor dots nor plus buttons around it. */
  private textEditing = false;
  /** Snapping hints of the gesture in progress (world). */
  private guides: Guide[] = [];
  /** What the pointer rests on (overlay outline, cursor); evaluated at most once per frame from `hoverInput`. */
  private hover: Hover = NO_HOVER;
  private hoverInput: PointerInput | null = null;
  private hoverDirty = false;
  private cursor = '';

  private frameHandle = 0;
  private cameraDirty = true;
  private zoomSent = 0;
  private fullCull = true;
  private transientDirty = false;
  private overlayDirty = true;
  private overlayEmpty = true;
  private lodPending = true;
  private willChangeOn = false;
  private lastActivity = 0;
  private lastTick = 0;
  private chained = false;
  private slowSince: number | null = null;
  private degraded = false;
  private gestureScale = 1;

  private readonly pointerSubs = new Set<(p: Point | null) => void>();
  private readonly cameraSubs = new Set<() => void>();
  private readonly navigateSubs = new Set<() => void>();
  private uiSnapshot: UiState;
  /** This person's snap-to-grid choice (P16); null follows the board's own setting. */
  private snapPref: boolean | null = null;
  private multiSelect = false;
  private readonly browseFirst: boolean;
  /** The person chose the select tool (toolbar, key): deselecting does not take it back to hand. */
  private pinnedSelect = false;
  /** The finger that went down in a tool or the hand: where, when, whether it travelled (a tap is a short, still press). */
  private tapDown: { id: number; p: Point; moved: boolean } | null = null;
  private lastTap: { at: number; p: Point } | null = null;
  private doubleTapAt = -Infinity;
  private readonly uiSubs = new Set<() => void>();
  private readonly pointers = new Map<number, Point>();
  private readonly pinch = new PinchTracker();
  private primary: number | null = null;
  private mode: 'idle' | 'tool' | 'pan' | 'pinch' | 'crop' | 'comment' = 'idle';
  /** Commenters and above: the comment tool works even on a read-only board (it never writes to the document). */
  private commentable = false;
  private commentDown: Point | null = null;

  constructor(opts: ControllerOptions) {
    this.store = opts.store;
    this.browseFirst = opts.browseFirst ?? false;
    if (this.browseFirst) this.toolId = 'hand';
    // The toolbar renders before attach(): a read-only board must not show creation tools on its first frame.
    this.uiSnapshot = { tool: this.toolId, selection: [], readOnly: opts.store.readOnly, canComment: false, multiSelect: false };
    this.media = opts.media;
    this.clock = opts.clock ?? browserClock;
    this.dpr = opts.dpr ?? (() => window.devicePixelRatio || 1);
    this.decode = opts.decode ?? decodeImage;
    this.lodCap = opts.lodCap ?? 2560;
    this.sizeOverride = opts.viewportSize;
    this.camera = opts.initialCamera ?? { x: 0, y: 0, z: 1 };
    this.hasInitialCamera = !!opts.initialCamera;
  }

  /** Ref callback target for object elements (stable: safe to pass down as a prop). */
  register = (id: string, el: HTMLElement | null): void => {
    if (el) this.els.set(id, el);
    else this.els.delete(id);
    if (el && (this.bornUntil.get(id) ?? 0) > this.clock.now()) el.setAttribute('data-born', '');
    if (el && this.selection.includes(id)) this.markSection(id);
    this.fullCull = true;
    this.lodPending = true;
    this.schedule();
  };

  attach(dom: EditorElements): void {
    this.dom = dom;
    this.reader = Object.assign(createSceneReader(this.store, () => this.selection), {
      entered: () => this.entered,
      endpoints: (id: string) => {
        const d = this.store.graph().paths.get(id);
        return d ? { start: d.start, end: d.end } : null;
      },
      midpoint: (id: string) => this.store.graph().paths.get(id)?.mid ?? null,
      aspect: (id: string) => {
        const o = this.store.getObject(id);
        if (o?.type !== 'image') return null;
        const info = this.media.info?.(o.assetId);
        return info ? imageAspect(info, o.crop) : o.w / o.h;
      },
    });
    this.selectTool = createSelectTool(this.reader, () => this.camera, {
      snap: () => ({ enabled: true, grid: this.gridSize() }),
      viewport: () => {
        const { w, h } = this.viewportSize();
        return visibleRect(this.camera, w, h, 0);
      },
      canConnect: () => !this.store.readOnly,
    });
    this.tool = this.toolFor(this.toolId);
    // Read-only (viewer, revoked): only select and hand, no handles; the store's commands are no-ops anyway.
    this.unsubscribeReadOnly = this.store.subscribeReadOnly(() => {
      if (this.store.readOnly && !READ_ONLY_TOOLS.has(this.toolId) && this.toolId !== 'comment') this.toolId = this.browseFirst ? 'hand' : 'select';
      this.overlayDirty = true;
      this.schedule();
      this.emitUi();
    });
    this.emitUi();
    this.unsubscribeList = this.store.subscribeList(() => {
      // React re-renders a changed object at its document frame: put the transient frame back on the next tick.
      if (this.transient.size) this.transientDirty = true;
      this.fullCull = true;
      this.lodPending = true;
      this.overlayDirty = true;
      this.schedule();
    });
    // Board background (shared setting): restyled with the camera, outside React.
    this.unsubscribeMeta = this.store.subscribeMeta(() => {
      this.cameraDirty = true;
      this.schedule();
      if (this.snapPref === null) this.emitUi(); // the board's snap setting is the default of the toggle
    });
    if (!this.hasInitialCamera) this.camera = this.fitCamera();
    const v = dom.viewport;
    v.addEventListener('wheel', this.onWheel, { passive: false });
    v.addEventListener('pointerdown', this.onDown);
    v.addEventListener('pointermove', this.onMove);
    v.addEventListener('pointerleave', this.onLeave);
    v.addEventListener('pointerup', this.onUp);
    v.addEventListener('pointercancel', this.onUp);
    v.addEventListener('keydown', this.onKeyDown);
    v.addEventListener('dblclick', this.onDblClick);
    v.addEventListener('contextmenu', this.onContextMenu);
    v.addEventListener('gesturestart', this.onGestureStart); // Safari sends trackpad pinch as gesture events
    v.addEventListener('gesturechange', this.onGestureChange);
    window.addEventListener('resize', this.onResize);
    this.setCamera(this.camera);
  }

  detach(): void {
    const dom = this.dom;
    if (!dom) return;
    const v = dom.viewport;
    v.removeEventListener('wheel', this.onWheel);
    v.removeEventListener('pointerdown', this.onDown);
    v.removeEventListener('pointermove', this.onMove);
    v.removeEventListener('pointerleave', this.onLeave);
    v.removeEventListener('pointerup', this.onUp);
    v.removeEventListener('pointercancel', this.onUp);
    v.removeEventListener('keydown', this.onKeyDown);
    v.removeEventListener('dblclick', this.onDblClick);
    v.removeEventListener('contextmenu', this.onContextMenu);
    this.cancelLongPress();
    v.removeEventListener('gesturestart', this.onGestureStart);
    v.removeEventListener('gesturechange', this.onGestureChange);
    window.removeEventListener('resize', this.onResize);
    if (this.frameHandle) this.clock.caf(this.frameHandle);
    this.frameHandle = 0;
    this.unsubscribeList?.();
    this.unsubscribeReadOnly?.();
    this.unsubscribeMeta?.();
    this.reader?.destroy();
    this.dom = null;
    this.reader = null;
    this.tool = null;
    this.selectTool = null;
    this.pointers.clear();
    this.mode = 'idle';
  }

  /** A camera move by the user (wheel, pan, pinch, keys): also tells presence to stop following anyone. */
  setCamera(c: Camera): void {
    this.followCamera(c);
    this.navigateSubs.forEach((cb) => cb());
  }

  /**
   * After a whole-file import (P12, STATUS 36): fits the view on what landed on this page, clear of the bars, unless
   * all of it is already on screen. Objects added to other pages (a file of several pages) leave the view alone.
   */
  revealObjects(ids: readonly string[]): void {
    const g = this.store.graph();
    const here = ids.filter((id) => g.world.has(id) && !g.hidden.has(id));
    const bounds = unionRects(here.map((id) => obbBounds(obbOf(g.world.get(id)!))));
    if (!bounds) return;
    const { w, h } = this.viewportSize();
    const view = visibleRect(this.camera, w, h, 0);
    if (bounds.minX >= view.minX && bounds.minY >= view.minY && bounds.maxX <= view.maxX && bounds.maxY <= view.maxY) return;
    this.setCamera(this.fitCamera(here, true));
  }

  /** Pans just enough to show `box` (world) above a virtual keyboard hiding `inset` px at the bottom (spec §13). */
  revealBox(box: Box, inset: number): void {
    const next = revealAboveKeyboard(this.camera, box, this.viewportSize(), inset);
    if (next !== this.camera) this.setCamera(next);
  }

  private pendingReveal: number | null = null;

  /**
   * Phones: the selection sheet covers the lower part of the view; pan so the selected objects stay above it. Held
   * back while a finger is down (an object being dragged must not slide away under it), applied when it lifts.
   */
  revealSelection(inset: number): void {
    this.pendingReveal = inset;
    if (this.mode === 'idle') this.flushReveal();
  }

  private flushReveal(): void {
    const inset = this.pendingReveal;
    this.pendingReveal = null;
    if (inset === null || this.selection.length === 0) return;
    const boxes = this.selection.map((id) => this.store.graph().world.get(id)).filter((w): w is NonNullable<typeof w> => !!w);
    if (boxes.length === 0) return;
    const x = Math.min(...boxes.map((w) => w.x));
    const y = Math.min(...boxes.map((w) => w.y));
    this.revealBox({ x, y, w: Math.max(...boxes.map((w) => w.x + w.w)) - x, h: Math.max(...boxes.map((w) => w.y + w.h)) - y }, inset);
  }

  /** A camera move driven by presence (following a peer). */
  followCamera(c: Camera): void {
    this.embeds.activate(null); // no iframe stays live during a pan or a zoom (it would swallow the gesture)
    this.camera = c;
    this.cameraMovedAt = this.clock.now();
    this.updateBusy();
    this.cameraDirty = true;
    this.lodPending = true;
    this.lastActivity = this.clock.now();
    this.schedule();
    this.cameraSubs.forEach((cb) => cb());
  }

  /** World position of the pointer over the canvas (hover included); null when it leaves. For presence. */
  subscribePointer = (cb: (p: Point | null) => void): (() => void) => {
    this.pointerSubs.add(cb);
    return () => void this.pointerSubs.delete(cb);
  };

  subscribeCamera = (cb: () => void): (() => void) => {
    this.cameraSubs.add(cb);
    return () => void this.cameraSubs.delete(cb);
  };

  subscribeNavigate = (cb: () => void): (() => void) => {
    this.navigateSubs.add(cb);
    return () => void this.navigateSubs.delete(cb);
  };

  setSelection(ids: readonly string[]): void {
    this.tabbedTo = null;
    for (const id of this.selection) if (!ids.includes(id)) this.els.get(id)?.removeAttribute('data-section-selected');
    this.selection = [...ids];
    for (const id of ids) this.markSection(id);
    if (this.activeEmbed && !ids.includes(this.activeEmbed)) this.embeds.activate(null);
    if (this.entered !== null) {
      const g = this.store.graph();
      const inside = (id: string) => ancestorsOf(g, id).includes(this.entered!);
      if (ids.length === 0 || !ids.every(inside)) this.entered = null;
    }
    this.overlayDirty = true;
    this.schedule();
    this.settleTool();
    this.emitUi();
  }

  /**
   * Mobile: the select tool picked up by itself (a double tap, a hold, a freshly created object) lasts as long as
   * something is selected; an empty selection hands the finger back to the hand, between gestures.
   */
  private settleTool(): void {
    if (!this.browseFirst || this.pinnedSelect || this.toolId !== 'select' || this.selection.length > 0 || this.mode !== 'idle') return;
    this.toolId = 'hand';
    this.clearHover();
  }

  /**
   * A tool chosen by the person (toolbar, shortcut) rather than switched by a gesture: choosing select keeps it until
   * hand is chosen; creation tools leave that choice as it was.
   */
  pickTool(id: ToolId): void {
    if (id === 'select') this.pinnedSelect = true;
    else if (id === 'hand') this.pinnedSelect = false;
    this.setTool(id);
  }

  /**
   * After the store showed another page (P14 Ruling 13): nothing of the old page stays selected, edited or entered, and
   * the camera is the page's last one, else fitted to its content. A 160 ms fade (none with reduced motion) eases the cut.
   */
  switchPage(camera: Camera | null, opts: { reducedMotion?: boolean } = {}): void {
    if (this.liveOpen) this.endLive(true); // a drag of an object that just left the screen (a peer deleted its page)
    this.setSelection([]);
    this.setTextEditing(false);
    this.entered = null;
    // Not a navigation of the view (that would stop following someone whose page this is): presence decides (P14).
    this.followCamera(camera ?? this.fitCamera());
    const world = this.dom?.world;
    if (world && typeof world.animate === 'function' && !(opts.reducedMotion ?? motionOff())) {
      world.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
    }
  }

  /** A selected section's title pill takes its colour (CSS on the attribute, never a React prop of a world object). */
  private markSection(id: string): void {
    if (this.store.getObject(id)?.type === 'section') this.els.get(id)?.setAttribute('data-section-selected', '');
  }

  /** Whether the user may comment (role ≥ commenter); turning it off leaves the comment tool. */
  setCommentable(on: boolean): void {
    if (on === this.commentable) return;
    this.commentable = on;
    if (!on && this.toolId === 'comment') this.setTool('select');
    else this.emitUi();
  }

  /** The image tool is a one-shot action (file picker); the other tools stay armed until used or Escape. */
  /** Zoom controls: an exact level, around the centre of the view. */
  zoomTo(z: number): void {
    const { w, h } = this.viewportSize();
    this.setCamera(zoomAt(this.camera, { x: w / 2, y: h / 2 }, z / this.camera.z));
  }

  /** Shape submenu of the toolbar: the next shape drawn has this kind. */
  setShapeKind(kind: (typeof SHAPES)[number]): void {
    this.shapeKind = kind;
    this.setTool('shape');
  }

  setTool(id: ToolId): void {
    if (id === 'comment' ? !this.commentable : this.store.readOnly && !READ_ONLY_TOOLS.has(id)) return;
    if (id === 'image') {
      this.handlers.onPickImage?.();
      id = 'select';
    }
    this.toolId = id;
    this.embeds.activate(null);
    if (this.selectTool) this.tool = this.toolFor(id);
    this.clearHover();
    this.emitUi();
  }

  subscribeUi = (cb: () => void): (() => void) => {
    this.uiSubs.add(cb);
    return () => void this.uiSubs.delete(cb);
  };

  ui = (): UiState => this.uiSnapshot;

  /** Snapping to the grid (P16): this person's choice, else the board's setting. Read with `subscribeUi`. */
  snapEnabled = (): boolean => this.snapPref ?? this.store.meta().grid.snap;

  /** `null`: back to the board's setting. */
  setSnapEnabled(on: boolean | null): void {
    this.snapPref = on;
    this.emitUi();
  }

  /** The grid step objects snap to, or null when snapping is off. */
  private gridSize(): number | null {
    return this.snapEnabled() ? this.store.meta().grid.size : null;
  }

  /** `offset` adjusted so a group whose top-left corner is `min` lands on the grid (paste, duplicate); unchanged when off. */
  snapOffset(min: Point, offset: Point): Point {
    const g = this.gridSize();
    if (!g) return offset;
    return { x: snapToGrid(min.x + offset.x, g) - min.x, y: snapToGrid(min.y + offset.y, g) - min.y };
  }

  /** True during a gesture (drag, resize, pan, pinch…) and until 200 ms after the last camera move (P5f). */
  busy = (): boolean => this.busyNow;

  /** Called when `busy()` changes (never once per frame). */
  subscribeBusy = (cb: () => void): (() => void) => {
    this.busySubs.add(cb);
    return () => void this.busySubs.delete(cb);
  };

  private updateBusy(): void {
    const settling = this.clock.now() - this.cameraMovedAt < BUSY_SETTLE_MS;
    const next = this.pointers.size > 0 || this.mode !== 'idle' || this.cropping !== null || settling;
    if (settling) this.schedule(); // the frame loop sees the camera settle
    if (next === this.busyNow) return;
    this.busyNow = next;
    this.busySubs.forEach((cb) => cb());
  }

  /** Screen box (viewport pixels) around the selection, rotated objects included; null without a selection. */
  selectionScreenRect(): { x: number; y: number; w: number; h: number } | null {
    const g = this.store.graph();
    const frames = this.selection.flatMap((id) => (g.world.get(id) ? [g.world.get(id)!] : []));
    if (!frames.length) return null;
    const b = frames.map((f) => obbBounds(obbOf(f))).reduce((a, r) => ({
      minX: Math.min(a.minX, r.minX), minY: Math.min(a.minY, r.minY), maxX: Math.max(a.maxX, r.maxX), maxY: Math.max(a.maxY, r.maxY),
    }));
    const a = worldToScreen(this.camera, { x: b.minX, y: b.minY });
    const z = this.camera.z;
    return { x: a.x, y: a.y, w: (b.maxX - b.minX) * z, h: (b.maxY - b.minY) * z };
  }

  /** Touch multi-selection: while on, a tap toggles the object under it, as shift-click does. */
  setMultiSelect(on: boolean): void {
    if (this.multiSelect === on) return;
    this.multiSelect = on;
    // Taps add and remove objects: that is the select tool, whatever the view was doing (mobile opens in hand).
    if (on && this.toolId === 'hand' && this.browseFirst) this.setTool('select');
    this.emitUi();
    if (!on) this.settleTool();
  }

  focus(): void {
    this.dom?.viewport.focus({ preventScroll: true });
  }

  /** World point under a client (page) position, e.g. a drop. */
  worldAt(clientX: number, clientY: number): Point {
    return screenToWorld(this.camera, this.local({ clientX, clientY }));
  }

  /**
   * Keyboard focus walk (P11 Ruling 6): selects the next (1) or previous (-1) object in reading order and pans to it
   * when it is off screen. False at either end: the caller lets the key move the focus out of the canvas.
   */
  focusNext(dir: 1 | -1): boolean {
    const g = this.store.graph();
    const order = keyboardOrder(
      this.store.list().flatMap((o) => {
        const w = g.world.get(o.id);
        return w ? [{ id: o.id, parentId: o.parentId, type: o.type, x: w.x, y: w.y, w: w.w, h: w.h }] : [];
      }),
    );
    const at = this.selection.length ? order.indexOf(this.selection[0]!) : -1;
    const next = at === -1 ? (dir === 1 ? 0 : order.length - 1) : at + dir;
    const id = order[next];
    if (id === undefined) return false;
    this.setSelection([id]);
    this.tabbedTo = id;
    const w = g.world.get(id)!;
    const { w: vw, h: vh } = this.viewportSize();
    const a = worldToScreen(this.camera, { x: w.x, y: w.y });
    const b = worldToScreen(this.camera, { x: w.x + w.w, y: w.y + w.h });
    if (a.x < 0 || a.y < 0 || b.x > vw || b.y > vh) {
      const z = this.camera.z;
      this.setCamera({ z, x: w.x + w.w / 2 - vw / 2 / z, y: w.y + w.h / 2 - vh / 2 / z });
    }
    const o = this.store.getObject(id)!;
    const text = 'text' in o && typeof o.text === 'string' ? o.text : o.type === 'section' ? o.name : o.type === 'tasklist' ? o.title : '';
    this.handlers.onFocusObject?.({ id, type: o.type, text: text.trim(), index: next + 1, total: order.length });
    return true;
  }

  /** Shift+F10 or the Menu key: the context menu at the selection's centre, or at the view's centre without one. */
  private openMenuAtSelection(): void {
    const g = this.store.graph();
    const boxes = this.selection.flatMap((id) => {
      const w = g.world.get(id);
      return w ? [w] : [];
    });
    const centre = boxes.length
      ? worldToScreen(this.camera, {
          x: (Math.min(...boxes.map((b) => b.x)) + Math.max(...boxes.map((b) => b.x + b.w))) / 2,
          y: (Math.min(...boxes.map((b) => b.y)) + Math.max(...boxes.map((b) => b.y + b.h))) / 2,
        })
      : (({ w, h }) => ({ x: w / 2, y: h / 2 }))(this.viewportSize());
    const r = this.dom?.viewport.getBoundingClientRect();
    this.handlers.onContextMenu?.({ x: centre.x + (r?.left ?? 0), y: centre.y + (r?.top ?? 0), cell: null });
  }

  /** The viewport's size in pixels. */
  viewSize(): { w: number; h: number } {
    return this.viewportSize();
  }

  viewCenter(): Point {
    const { w, h } = this.viewportSize();
    return screenToWorld(this.camera, { x: w / 2, y: h / 2 });
  }

  runCommand(id: CommandId): void {
    const sel = [...this.selection];
    const { w, h } = this.viewportSize();
    const center = { x: w / 2, y: h / 2 };
    if (id === 'copyStyle' || id === 'pasteStyle') return this.styleCommand(id);
    switch (id) {
      case 'undo':
        return this.store.undo();
      case 'redo':
        return this.store.redo();
      case 'duplicate': {
        if (!sel.length) return;
        const boxes = sel.map((id) => this.store.getObject(id)).filter((o) => o && 'w' in o) as { x: number; y: number }[];
        const min = boxes.length ? { x: Math.min(...boxes.map((b) => b.x)), y: Math.min(...boxes.map((b) => b.y)) } : null;
        const copies = this.store.commands.duplicate(sel, min ? this.snapOffset(min, DUPLICATE_OFFSET) : DUPLICATE_OFFSET);
        this.born(copies);
        this.setSelection(copies);
        return;
      }
      case 'delete': {
        const ids = this.unlocked(sel);
        if (ids.length) {
          this.store.commands.deleteObjects(ids);
          this.setSelection(sel.filter((id) => !ids.includes(id)));
        }
        return;
      }
      case 'selectAll':
        return this.setSelection(this.store.list().filter((o) => !this.isLocked(o.id)).map((o) => o.id));
      case 'bringForward':
        return this.store.commands.reorder(sel, 'forward');
      case 'sendBackward':
        return this.store.commands.reorder(sel, 'backward');
      case 'zoomFit':
        return this.setCamera(this.fitCamera(undefined, true));
      case 'zoomSelection':
        return this.setCamera(this.fitCamera(sel, true));
      case 'fitToContent': {
        const one = sel.length === 1 ? this.store.getObject(sel[0]!) : undefined;
        if (one?.type === 'section') this.discrete(() => this.store.commands.fitSectionToContent(one.id));
        return;
      }
      case 'renameSection': {
        // F2 (P5e): a single section's title; nothing on a locked section or a read-only board.
        const one = sel.length === 1 ? this.store.getObject(sel[0]!) : undefined;
        if (one?.type === 'section' && !this.isLocked(one.id) && !this.store.readOnly) this.handlers.onEditSectionTitle?.(one.id);
        return;
      }
      case 'zoomReset':
        return this.setCamera(zoomAt(this.camera, center, 1 / this.camera.z));
      case 'zoomIn':
        return this.setCamera(zoomAt(this.camera, center, ZOOM_STEP));
      case 'zoomOut':
        return this.setCamera(zoomAt(this.camera, center, 1 / ZOOM_STEP));
      case 'alignLeft':
      case 'alignHCenter':
      case 'alignRight':
      case 'alignTop':
      case 'alignVCenter':
      case 'alignBottom': {
        const mode = ({ alignLeft: 'left', alignHCenter: 'hcenter', alignRight: 'right', alignTop: 'top', alignVCenter: 'vcenter', alignBottom: 'bottom' } as const)[id];
        const ids = this.unlocked(sel);
        if (ids.length >= 2) this.discrete(() => this.store.commands.align(ids, mode));
        return;
      }
      case 'distributeH':
      case 'distributeV':
        return this.discrete(() => this.store.commands.distribute(this.unlocked(sel), id === 'distributeH' ? 'x' : 'y'));
      case 'arrange':
        if (this.canArrange()) this.handlers.onArrange?.();
        return;
      case 'palette':
        return this.handlers.onPalette?.();
      case 'nextPage':
        return this.handlers.onPage?.(1);
      case 'prevPage':
        return this.handlers.onPage?.(-1);
      case 'bringToFront':
        return this.store.commands.reorder(sel, 'front');
      case 'sendToBack':
        return this.store.commands.reorder(sel, 'back');
      case 'toggleLock': {
        const objs = sel.map((s) => this.store.getObject(s)).filter((o) => !!o);
        if (objs.length) this.store.commands.setProps(sel, { locked: !objs.every((o) => o.locked) });
        this.overlayDirty = true;
        this.schedule();
        return;
      }
      case 'copy':
      case 'cut':
        document.execCommand?.(id); // fires the copy/cut events the clipboard hook handles
        return;
      case 'paste': {
        const payload = lastCopied();
        if (!payload || this.store.readOnly) return;
        const b = payloadBounds(payload);
        const offset = pasteOffset(payload, this.pointerWorld ?? this.viewCenter());
        const ids = this.store.commands.insertObjects(payload.objects, { offset: b ? this.snapOffset({ x: b.minX, y: b.minY }, offset) : offset });
        this.born(ids);
        if (ids.length) this.setSelection(ids.filter((x) => !ids.includes(this.store.getObject(x)?.parentId ?? '')));
        return;
      }
      case 'group': {
        const g = this.store.commands.group(sel);
        if (g) this.setSelection([g]);
        return;
      }
      case 'ungroup': {
        const kids = this.store.commands.ungroup(sel);
        // An empty section leaves nothing behind: the selection still drops it.
        if (kids.length || sel.some((id) => !this.store.getObject(id))) this.setSelection([...sel.filter((id) => this.store.getObject(id)), ...kids]);
        return;
      }
    }
  }

  setTextEditing(on: boolean): void {
    if (this.textEditing === on) return;
    this.textEditing = on;
    this.overlayDirty = true;
    this.schedule();
  }

  canPasteStyle(): boolean {
    return this.styleClip !== null;
  }

  /** Copy style (from the single selected object) and paste style (onto the unlocked selection, one undo step). */
  private styleCommand(id: 'copyStyle' | 'pasteStyle'): void {
    const sel = this.selection;
    if (id === 'copyStyle') {
      const one = sel.length === 1 ? this.store.getObject(sel[0]!) : undefined;
      if (!one) return;
      this.styleClip = styleOf(one);
      this.handlers.onNotice?.('styleCopied');
      return;
    }
    if (!this.styleClip) return void this.handlers.onNotice?.('nothingToPaste');
    const ids = this.unlocked(sel);
    if (ids.length === 0) return;
    const clip = this.styleClip;
    this.discrete(() => this.store.commands.pasteStyle(ids, clip));
    this.handlers.onNotice?.('stylePasted');
  }

  /** Default object of a creation tool, centred on `at`. */
  createAt(tool: ToolId, at: Point): string | null {
    const base = { parentId: null, rotation: 0, locked: false, style: {} };
    const g = this.gridSize();
    // Centred on the point, or with its top-left corner on the grid when snapping is on (P16).
    const box = (w: number, h: number) => ({ x: g ? snapToGrid(at.x - w / 2, g) : at.x - w / 2, y: g ? snapToGrid(at.y - h / 2, g) : at.y - h / 2, w, h });
    let data: NewObject;
    if (tool === 'text') data = { ...base, ...box(240, 48), type: 'text', text: this.defaultText, fontSize: 24 };
    else if (tool === 'sticky') data = { ...base, ...box(200, 200), type: 'sticky', text: '', color: DEFAULT_STICKY };
    else if (tool === 'shape') data = { ...base, ...box(160, 100), type: 'shape', shape: this.shapeKind };
    else if (tool === 'arrow') data = { ...base, ...box(160, 1), type: 'arrow', points: [{ x: 0, y: 0 }, { x: 160, y: 0 }] };
    else if (tool === 'table') {
      const t = newTable(3, 3, at);
      data = g && 'x' in t ? { ...t, x: snapToGrid(t.x, g), y: snapToGrid(t.y, g) } : t;
    }
    else if (tool === 'tasklist') {
      const t = newTaskList(at);
      data = g && 'x' in t ? { ...t, x: snapToGrid(t.x, g), y: snapToGrid(t.y, g) } : t;
    }
    else if (tool === 'section') return this.createSection(tool, box(640, 400));
    else if (tool === 'frame') return this.createSection(tool, box(1280, 720));
    else return null;
    let id: string | null = null;
    this.discrete(() => {
      id = this.store.commands.createObject(data);
      if (id) this.adoptIntoSections([id]);
    });
    if (id) this.born([id]);
    return id;
  }

  /** Object › Task list (P16-5): a list at the view's centre, selected, its title being edited; one undo step. */
  insertTaskList(): string | null {
    if (this.store.readOnly) return null;
    let id: string | null = null;
    this.discrete(() => {
      id = this.createAt('tasklist', this.viewCenter());
    });
    if (id) this.setSelection([id]);
    this.editCreated('tasklist', id);
    return id;
  }

  /**
   * A note or a text just placed is typed into at once; a task list starts with its title (P16-5 Ruling 9). Placed by a
   * press (`pressed`), the list's editor opens on the next frame: the press would otherwise take the focus back to the
   * canvas once the editor has it, and close it.
   */
  private editCreated(tool: ToolId, id: string | null, pressed = false): void {
    if (!id) return;
    if (tool === 'sticky' || tool === 'text') this.handlers.onEditText?.(id);
    else if (tool === 'tasklist') {
      if (pressed) this.clock.raf(() => this.handlers.onEditTask?.(id, 'title'));
      else this.handlers.onEditTask?.(id, 'title');
    }
  }

  /** A shape of the current kind filling the world box dragged out with the shape tool (P5f): one undo step. */
  private createShape(b: Box): string | null {
    let id: string | null = null;
    this.discrete(() => {
      id = this.store.commands.createObject({ parentId: null, rotation: 0, locked: false, style: {}, ...b, type: 'shape', shape: this.shapeKind });
      if (id) this.adoptIntoSections([id]);
    });
    return id;
  }

  /**
   * Answers the quick-create menu: the object (default size of its tool) placed so the middle of its side facing the
   * connector's source sits on the drop point, and the connector to that side, in one undo step. Sticky notes and
   * texts open in edition.
   */
  quickCreate(kind: 'sticky' | 'shape' | 'text'): string | null {
    const q = this.quick;
    this.quick = null;
    this.linkPreview = null;
    this.linkTarget = null;
    this.overlayDirty = true;
    this.schedule();
    if (!q || this.store.readOnly) return null;
    const [w, h] = kind === 'sticky' ? [200, 200] : kind === 'shape' ? [160, 100] : [240, 48];
    const dx = q.from.x - q.at.x;
    const dy = q.from.y - q.at.y;
    const side: Side = Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? 'right' : 'left') : dy >= 0 ? 'bottom' : 'top';
    const center = {
      x: q.at.x + (side === 'left' ? w / 2 : side === 'right' ? -w / 2 : 0),
      y: q.at.y + (side === 'top' ? h / 2 : side === 'bottom' ? -h / 2 : 0),
    };
    const anchor = { nx: side === 'right' ? 1 : side === 'left' ? 0 : 0.5, ny: side === 'bottom' ? 1 : side === 'top' ? 0 : 0.5 };
    let id: string | null = null;
    this.discrete(() => {
      const kindBefore = this.shapeKind;
      this.shapeKind = 'rectangle';
      id = this.createAt(kind, center);
      this.shapeKind = kindBefore;
      if (!id) return;
      this.store.commands.createObject({
        type: 'connector', start: q.from, end: { objectId: id, anchor, x: q.at.x, y: q.at.y }, routing: 'elbow', startCap: 'none', endCap: 'arrow',
        parentId: null, x: 0, y: 0, w: 1, h: 1, rotation: 0, locked: false, style: {},
      });
    });
    if (!id) return null;
    this.setSelection([id]);
    if (kind !== 'shape') this.handlers.onEditText?.(id);
    return id;
  }

  /**
   * A sticky note next to `id` in direction `dir` (same color, size and parent), in the first free slot: one undo
   * step, selected and opened in edition.
   */
  createNeighbor(id: string, dir: Side): string | null {
    const src = this.store.getObject(id);
    const g = this.store.graph();
    const w = g.world.get(id);
    if (src?.type !== 'sticky' || !w || this.store.readOnly) return null;
    const occupied = this.store
      .list()
      .filter((o) => o.id !== id && o.type !== 'connector' && o.type !== 'section' && o.type !== 'group')
      .map((o) => g.world.get(o.id))
      .filter((f): f is NonNullable<typeof f> => !!f)
      .map((f) => obbBounds(obbOf(f)));
    const slot = neighborSlot(w, dir, occupied);
    const origin = src.parentId ? g.world.get(src.parentId) : undefined;
    let made: string | null = null;
    this.discrete(() => {
      made = this.store.commands.createObject({
        type: 'sticky', text: '', color: src.color, parentId: src.parentId,
        x: slot.x - (origin?.x ?? 0), y: slot.y - (origin?.y ?? 0), w: slot.w, h: slot.h, rotation: 0, locked: false, style: {},
      }) || null;
    });
    if (!made) return null;
    this.born([made]);
    this.setSelection([made]);
    this.handlers.onEditText?.(made);
    return made;
  }

  /** The quick-create menu was dismissed: the drawn connector disappears, the document is untouched. */
  cancelQuickCreate(): void {
    this.quick = null;
    this.linkPreview = null;
    this.linkTarget = null;
    this.overlayDirty = true;
    this.schedule();
  }

  /** A section or a frame over `b` (world), adopting the board-level objects entirely inside it: one undo step. */
  /** Board-level objects (never connectors, nor `except`) entirely inside the world box `b`. */
  private enclosedBy(b: { x: number; y: number; w: number; h: number }, except?: string): string[] {
    const g = this.store.graph();
    return this.store
      .list()
      .filter((o) => o.id !== except && o.type !== 'connector' && (g.parent.get(o.id) ?? null) === null)
      .filter((o) => {
        const r = obbBounds(obbOf(g.world.get(o.id) ?? frameOf(o)));
        return r.minX >= b.x && r.minY >= b.y && r.maxX <= b.x + b.w && r.maxY <= b.y + b.h;
      })
      .map((o) => o.id);
  }

  /** After a section is resized: it adopts the board-level objects it now encloses and lets go of children left entirely outside. */
  private reframeSection(id: string): void {
    const o = this.store.getObject(id);
    const g = this.store.graph();
    const b = g.world.get(id);
    if (o?.type !== 'section' || o.parentId !== null || !b) return;
    const inside = this.enclosedBy(b, id);
    const outside = (g.children.get(id) ?? []).filter((c) => {
      const r = obbBounds(obbOf(g.world.get(c) ?? b));
      return r.maxX <= b.x || r.minX >= b.x + b.w || r.maxY <= b.y || r.minY >= b.y + b.h;
    });
    if (inside.length) this.store.commands.reparent(inside, id);
    if (outside.length) this.store.commands.reparent(outside, null);
  }

  private createSection(tool: 'section' | 'frame', b: { x: number; y: number; w: number; h: number }): string | null {
    const frame = tool === 'frame';
    const inside = this.enclosedBy(b);
    let id: string | null = null;
    this.discrete(() => {
      id = this.store.commands.createObject({
        ...b, type: 'section', name: '', fill: frame ? '#ffffff' : SECTION_COLORS[0].fill, clip: frame, collapsed: false,
        parentId: null, rotation: 0, locked: false, style: {},
      }) || null;
      if (!id) return;
      if (inside.length) this.store.commands.reparent(inside, id);
      this.adoptIntoSections([id]); // into a bigger section it fits in entirely
    });
    if (id) this.born([id]);
    return id;
  }

  private emitUi(): void {
    this.updateCursor();
    this.uiSnapshot = { tool: this.toolId, selection: this.selection, readOnly: this.store.readOnly, canComment: this.commentable, multiSelect: this.multiSelect };
    this.uiSubs.forEach((cb) => cb());
  }

  /** Locked objects stay selectable but never move, resize, rotate or get deleted. */
  private unlocked(ids: readonly string[]): string[] {
    if (this.store.readOnly) return [];
    return ids.filter((id) => this.store.getObject(id) && !this.isLocked(id));
  }

  /** Locked itself or inside a locked section (P5d: a locked section locks its content). */
  private isLocked(id: string): boolean {
    return isEffectivelyLocked(this.store.graph(), (x) => this.store.getObject(x), id);
  }

  /** Applies what a tool asked for. */
  apply(intents: Intent[]): void {
    for (const raw of intents) {
      const i = raw.kind === 'translate' || raw.kind === 'rotate' ? { ...raw, ids: this.unlocked(raw.ids) } : raw;
      if ((i.kind === 'translate' || i.kind === 'rotate') && i.ids.length === 0) continue;
      if (i.kind === 'resize' && !this.unlocked([i.id]).length) continue;
      switch (i.kind) {
        case 'translate': {
          const ids = i.copy ? this.copiesOf(i.ids) : i.ids;
          if (i.copy && i.commit) this.copies = null;
          // Commands are relative: write what the document does not have yet.
          const write = () => {
            const g = this.live;
            const [dx, dy] = [i.dx - g.dx, i.dy - g.dy];
            if (dx || dy) this.store.commands.moveObjects(ids, dx, dy);
            [g.dx, g.dy] = [i.dx, i.dy];
          };
          if (i.commit) {
            this.dropHint = null;
            this.commit(ids, () => (write(), this.pulse(this.adoptIntoSections(ids))));
          } else {
            this.preview(ids, (o) => ({ ...frameOf(o), x: o.x + i.dx, y: o.y + i.dy }), write);
            this.hintDrop(ids);
          }
          break;
        }
        case 'resize': {
          const write = () => this.store.commands.resizeObject(i.id, i.box);
          if (i.commit) {
            this.hintLeaving(null, null);
            this.commit([i.id], () => (write(), this.reframeSection(i.id)));
            break;
          }
          // A section's children are relative to it: they keep their place in the world while its origin moves (P5e).
          const kids = this.store.getObject(i.id)?.type === 'section' ? (this.store.graph().children.get(i.id) ?? []) : [];
          const start = this.origins.get(i.id) ?? this.store.getObject(i.id);
          const to = start ? this.toLocal(start, i.box) : null;
          this.preview([i.id, ...kids], (o) =>
            o.id === i.id ? { ...to!, rotation: o.rotation } : { ...frameOf(o), x: o.x - (to!.x - start!.x), y: o.y - (to!.y - start!.y) },
          write);
          if (kids.length) this.hintLeaving(i.id, i.box);
          break;
        }
        case 'rotate': {
          const write = () => {
            const g = this.live;
            if (i.deltaDeg - g.deg) this.store.commands.rotateObjects(i.ids, i.deltaDeg - g.deg, i.pivot);
            g.deg = i.deltaDeg;
          };
          if (i.commit) this.commit(i.ids, write);
          else this.preview(i.ids, (o) => rotateFrame(frameOf(o), i.deltaDeg, this.toLocal(o, i.pivot)), write);
          break;
        }
        case 'select': {
          const cur = new Set(this.selection);
          if (i.mode === 'replace') this.setSelection(i.ids);
          else if (i.mode === 'add') this.setSelection([...this.selection, ...i.ids.filter((id) => !cur.has(id))]);
          else this.setSelection([...this.selection.filter((id) => !i.ids.includes(id)), ...i.ids.filter((id) => !cur.has(id))]);
          break;
        }
        case 'marquee':
          this.marquee = i.rect;
          this.overlayDirty = true;
          this.schedule();
          break;
        case 'pan':
          this.setCamera(panBy(this.camera, i.dx, i.dy));
          break;
        case 'hover': {
          const h = this.hover;
          if (h.id === i.hover.id && h.handle === i.hover.handle && h.dot === i.hover.dot && h.plus === i.hover.plus && h.grab === i.hover.grab) break;
          // A section taken by its contour or title outlines itself in its colour (CSS on the attribute, P5e).
          if (h.grab && h.id) this.els.get(h.id)?.removeAttribute('data-section-hover');
          if (i.hover.grab && i.hover.id && this.store.getObject(i.hover.id)?.type === 'section') this.els.get(i.hover.id)?.setAttribute('data-section-hover', '');
          this.hover = i.hover;
          this.overlayDirty = true;
          this.updateCursor();
          break;
        }
        case 'guides':
          if (i.guides.length === 0 && this.guides.length === 0) break;
          this.guides = i.guides;
          this.overlayDirty = true;
          this.schedule();
          break;
        case 'cancel':
          if (this.bending) this.store.cancelGesture();
          this.bending = null;
          this.hintLeaving(null, null);
          this.dropHint = null;
          if (this.copies) {
            // The live gesture's revert below deletes the copies: back to the originals.
            const originals = this.copies.originals;
            this.copies = null;
            this.endLive(true);
            this.setSelection(originals);
          }
          this.guides = [];
          this.linkPreview = null;
          this.linkTarget = null;
          this.endLive(true);
          this.restore([...this.transient.keys()]);
          this.marquee = null;
          this.ghost = null;
          this.overlayDirty = true;
          this.schedule();
          break;
        case 'create': {
          const id = this.createAt(i.tool, i.at);
          if (id) this.setSelection([id]);
          if (BOX_TOOLS.has(i.tool)) this.setTool('select');
          break;
        }
        case 'boxPreview':
          this.ghost = i.rect ? { tool: i.tool, rect: i.rect } : null;
          this.overlayDirty = true;
          this.schedule();
          break;
        case 'createBox': {
          const id = i.tool === 'section' || i.tool === 'frame' ? this.createSection(i.tool, i.box) : i.tool === 'shape' ? this.createShape(i.box) : null;
          if (id) this.setSelection([id]);
          this.setTool('select');
          break;
        }
        case 'connect': {
          if (!i.commit) {
            this.previewLink(i.from, i.to);
            break;
          }
          this.linkPreview = null;
          this.linkTarget = null;
          this.overlayDirty = true;
          let id = '';
          // Its own undo step, even right after another edit (the store merges writes within 500 ms).
          this.discrete(() => {
            id = this.store.commands.createObject({
              type: 'connector', start: i.from, end: i.to, routing: 'elbow', startCap: 'none', endCap: 'arrow',
              parentId: null, x: 0, y: 0, w: 1, h: 1, rotation: 0, locked: false, style: {},
            });
          });
          this.setTool('select');
          if (id) this.setSelection([id]);
          break;
        }
        case 'bend': {
          const o = this.store.getObject(i.id);
          if (o?.type !== 'connector' || !this.unlocked([i.id]).length) break;
          const now = this.clock.now();
          if (!i.commit && this.bending && now - this.bending.at < LIVE_WRITE_MS) break;
          if (!this.bending) this.store.beginGesture();
          // Bending an elbow connector curves it.
          this.store.commands.setProps([i.id], o.routing === 'elbow' ? { bend: i.bend, routing: 'curved' } : { bend: i.bend });
          this.bending = { at: now };
          if (i.commit) {
            this.store.endGesture();
            this.bending = null;
          }
          this.overlayDirty = true;
          this.schedule();
          break;
        }
        case 'notice':
          this.handlers.onNotice?.(i.notice);
          if (i.id) this.shakeLock(i.id);
          break;
        case 'neighbor':
          this.createNeighbor(i.id, i.dir);
          break;
        case 'connectOpen': {
          if (!this.handlers.onQuickCreate || this.store.readOnly) {
            this.apply([{ kind: 'cancel' }]);
            break;
          }
          this.quick = { from: i.from, at: i.at };
          this.setTool('select');
          this.handlers.onQuickCreate({ at: i.at, screen: worldToScreen(this.camera, i.at) });
          break;
        }
        case 'reconnect': {
          const d = this.store.graph().paths.get(i.id);
          if (!i.commit) {
            if (d) this.previewLink(i.which === 'start' ? i.to : { objectId: null, anchor: null, ...d.start }, i.which === 'end' ? i.to : { objectId: null, anchor: null, ...d.end });
            break;
          }
          this.linkPreview = null;
          this.linkTarget = null;
          this.overlayDirty = true;
          if (this.unlocked([i.id]).length) this.store.commands.setConnectorEnd(i.id, i.which, i.to);
          break;
        }
        case 'command':
          this.runCommand(i.id);
          break;
      }
    }
  }

  /**
   * Transient frame for a gesture in progress, relative to the frames at gesture start: DOM every frame, and the
   * document at most every LIVE_WRITE_MS so peers follow the gesture (one undo step for the whole gesture).
   */
  /** Alt-drag: the copies of `ids`, made once (inside the live gesture, so the whole drag is one undo step). */
  private copiesOf(ids: string[]): string[] {
    if (this.copies) return this.copies.ids;
    if (ids.length === 0) return ids;
    if (!this.liveOpen) {
      this.store.beginGesture();
      this.liveOpen = true;
    }
    const made = this.store.commands.duplicate(ids, { x: 0, y: 0 });
    this.copies = { originals: ids, ids: made };
    this.setSelection(made);
    return made;
  }

  private preview(ids: string[], next: (origin: Readonly<ObjectData>) => Frame, write: () => void): void {
    for (const id of ids) {
      let o = this.origins.get(id);
      if (!o) {
        o = this.store.getObject(id);
        if (!o) continue;
        this.origins.set(id, o);
      }
      this.transient.set(id, next(o));
    }
    this.transientDirty = true;
    this.schedule();
    const now = this.clock.now();
    if (!this.liveOpen) {
      this.store.beginGesture();
      this.liveOpen = true;
    } else if (now - this.live.at < LIVE_WRITE_MS) return;
    this.live.at = now;
    write();
  }

  private toolFor(id: ToolId): Tool | null {
    if (!this.reader) return null;
    if (BOX_TOOLS.has(id)) return createBoxTool(id, () => this.camera, { ...(id === 'shape' ? { minWorld: MIN_SHAPE_WORLD } : {}), grid: () => this.gridSize() });
    if (id === 'connector') return createConnectorTool(this.reader, () => this.camera);
    return this.selectTool;
  }

  private previewLink(a: EndInput, b: EndInput): void {
    const side = (e: EndInput) => (e.anchor ? sideOf(e.anchor) : null);
    this.linkPreview = connectorPath(a, b, 'elbow', { a: side(a), b: side(b) }).samples;
    this.linkTarget = b.objectId ? b : null;
    this.overlayDirty = true;
    this.schedule();
  }

  /** Puts the world point `p` at the centre of the view (minimap). */
  centerOn(p: Point): void {
    const { w, h } = this.viewportSize();
    this.setCamera({ ...this.camera, x: p.x - w / 2 / this.camera.z, y: p.y - h / 2 / this.camera.z });
  }

  /** Fits a world box in the view (section list). */
  zoomToBox(box: { x: number; y: number; w: number; h: number }): void {
    const { w, h } = this.viewportSize();
    if (w > 0 && h > 0) this.setCamera(this.fitClearOfChrome({ minX: box.x, minY: box.y, maxX: box.x + box.w, maxY: box.y + box.h }, w, h));
  }

  /**
   * Fits `r` clear of the floating bars (`data-fit-inset`) and of corner panels such as the overview
   * (`data-fit-corner`), so a fit never leaves content under them.
   */
  private fitClearOfChrome(r: Rect, w: number, h: number): Camera {
    const v = this.dom?.viewport;
    if (!v || this.sizeOverride) return fitRect(r, w, h);
    const o = v.getBoundingClientRect();
    const boxes = (selector: string) =>
      [...v.ownerDocument.querySelectorAll(selector)].map((el) => {
        const b = el.getBoundingClientRect();
        return { left: b.left - o.left, top: b.top - o.top, right: b.right - o.left, bottom: b.bottom - o.top };
      });
    return fitClear(r, w, h, 48, insetsOf(boxes('[data-fit-inset]'), w, h), boxes('[data-fit-corner]'));
  }

  /** World rectangle currently on screen. */
  viewRect(): Rect {
    const { w, h } = this.viewportSize();
    return visibleRect(this.camera, w, h, 0);
  }

  /**
   * Runs a context-menu action on the selection (`cell`: the table cell it was opened on; `task`: the task of a list).
   * One undo step each.
   */
  runMenuAction(action: MenuAction, cell: ContextMenuRequest['cell'] = null, task: string | null = null): void {
    const sel = [...this.selection];
    const one = sel.length === 1 ? this.store.getObject(sel[0]!) : undefined;
    const c = this.store.commands;
    switch (action) {
      case 'copyStyle':
      case 'pasteStyle':
        return this.styleCommand(action);
      case 'lock':
      case 'unlock':
        c.setProps(sel, { locked: action === 'lock' });
        this.overlayDirty = true;
        this.schedule();
        return;
      case 'crop':
        if (one) this.startCrop(one.id);
        return;
      case 'resetCrop':
        if (one?.type === 'image') c.setCrop(one.id, FULL_CROP);
        return;
      case 'editText':
        if (one && !this.store.readOnly) this.handlers.onEditText?.(one.id);
        return;
      case 'tableAddRow':
      case 'tableAddCol':
      case 'tableDeleteRow':
      case 'tableDeleteCol': {
        if (one?.type !== 'table') return;
        const row = cell ? one.rows.findIndex((r) => r.id === cell.rowId) : -1;
        const col = cell ? one.cols.findIndex((k) => k.id === cell.colId) : -1;
        if (action === 'tableAddRow') c.tableInsertRow(one.id, row >= 0 ? row + 1 : one.rows.length);
        else if (action === 'tableAddCol') c.tableInsertCol(one.id, col >= 0 ? col + 1 : one.cols.length);
        else if (action === 'tableDeleteRow' && cell) c.tableDeleteRow(one.id, cell.rowId);
        else if (action === 'tableDeleteCol' && cell) c.tableDeleteCol(one.id, cell.colId);
        return;
      }
      case 'taskAdd':
      case 'taskCompact':
      case 'taskToggle':
      case 'taskMoveUp':
      case 'taskMoveDown':
      case 'taskIndent':
      case 'taskOutdent':
      case 'taskDelete': {
        if (one?.type !== 'tasklist' || this.store.readOnly || this.isLocked(one.id)) return;
        if (action === 'taskAdd') {
          const made = c.taskAdd(one.id, one.items.length);
          if (made) this.handlers.onEditTask?.(one.id, { itemId: made });
          return;
        }
        if (action === 'taskCompact') return c.setProps([one.id], { compact: !one.compact });
        const i = one.items.findIndex((t) => t.id === task);
        const t = one.items[i];
        if (!t) return;
        if (action === 'taskToggle') c.taskSetDone(one.id, t.id, !t.done);
        else if (action === 'taskMoveUp') c.taskMove(one.id, t.id, i - 1);
        else if (action === 'taskMoveDown') c.taskMove(one.id, t.id, i + 1);
        else if (action === 'taskIndent') c.taskIndent(one.id, t.id, t.indent + 1);
        else if (action === 'taskOutdent') c.taskIndent(one.id, t.id, t.indent - 1);
        else c.taskDelete(one.id, t.id);
        return;
      }
      case 'openLink': {
        const href = one && (one.type === 'link' || one.type === 'embed') ? safeHref(one.url) : null;
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      case 'collapse':
      case 'expand':
        if (one?.type === 'section') c.setProps([one.id], { collapsed: action === 'collapse' });
        return;
      case 'zoomToSelection':
        return this.runCommand('zoomSelection');
      case 'fitToContent':
        return this.runCommand('fitToContent');
      case 'removeSection':
        return this.runCommand('ungroup');
      case 'exportSelection':
      case 'exportCsv':
      case 'importFile':
      case 'moveToPage':
        return; // dialogs and file pickers belong to the Editor
      default:
        return this.runCommand(action);
    }
  }

  /** A link card or an embed for `url` at `at` (default: the centre of the view), selected; false for an invalid URL. */
  createLink(url: string, at: Point = this.viewCenter()): boolean {
    if (this.store.readOnly) return false;
    const data = linkOrEmbedFor(url, at);
    if (!data) return false;
    const id = this.store.commands.createObject(data);
    if (id) {
      this.born([id]);
      this.setSelection([id]);
    }
    return !!id;
  }

  /** A flowchart template at the centre of the view, selected: one undo step. */
  insertTemplate(kind: FlowchartTemplate, labels: FlowchartLabels): string[] {
    if (this.store.readOnly) return [];
    const ids = this.store.commands.insertObjects(flowchartTemplate(kind, this.viewCenter(), labels));
    this.born(ids);
    this.setTool('select');
    this.setSelection(ids);
    return ids;
  }

  /** Crop mode on an unrotated, unlocked image; nothing is written before `endCrop(true)`. */
  startCrop(id: string): void {
    const o = this.store.getObject(id);
    if (!o || o.type !== 'image' || this.isLocked(o.id) || o.rotation % 360 !== 0 || this.store.readOnly) return;
    this.cropping = id;
    this.cropDraft = o.crop;
    this.setSelection([id]);
    this.updateBusy();
  }

  endCrop(commit: boolean): void {
    const id = this.cropping;
    const draft = this.cropDraft;
    this.cropping = null;
    this.cropDraft = null;
    this.cropHandle = null;
    if (commit && id && draft) {
      const o = this.store.getObject(id);
      if (o?.type === 'image' && JSON.stringify(o.crop) !== JSON.stringify(draft)) this.store.commands.setCrop(id, draft);
    }
    this.overlayDirty = true;
    this.schedule();
    this.updateBusy();
  }

  /** World box of the whole source image and of the draft crop, while cropping. */
  private cropGeometry(): { full: { x: number; y: number; w: number; h: number }; rect: OBB } | null {
    const o = this.cropping ? this.store.getObject(this.cropping) : undefined;
    const w = this.cropping ? this.store.graph().world.get(this.cropping) : undefined;
    if (!o || o.type !== 'image' || !w || !this.cropDraft) return null;
    const full = fullImageBox(w, o.crop);
    const d = this.cropDraft;
    const rect = { cx: full.x + (d.x + d.w / 2) * full.w, cy: full.y + (d.y + d.h / 2) * full.h, w: d.w * full.w, h: d.h * full.h, rotation: 0 };
    return { full, rect };
  }

  /**
   * A single unlocked section (its content), or at least two unlocked objects. React callers pass the selection they
   * render (the compiler memoizes a call that only depends on the controller).
   */
  canArrange(ids: readonly string[] = this.selection): boolean {
    return this.arrangeSection(ids) !== null || this.unlocked(ids).length >= 2;
  }

  private arrangeSection(ids: readonly string[] = this.selection): string | null {
    const [id] = ids;
    const o = ids.length === 1 && id ? this.store.getObject(id) : undefined;
    return o?.type === 'section' && !this.isLocked(o.id) && !this.store.readOnly ? o.id : null;
  }

  /**
   * Shows where "Arrange" would put the selection, or a section's content and size (DOM only: the document is untouched
   * until applyArrange). The world carries `data-arranging` meanwhile: objects glide to their place (CSS).
   */
  previewArrange(mode: ArrangeMode, gap: number): void {
    this.restore([...this.transient.keys()]);
    this.dom?.world.setAttribute('data-arranging', '');
    const g = this.store.graph();
    const section = this.arrangeSection();
    if (section) {
      const plan = planSectionArrangement(g, (id) => this.store.getObject(id), section, mode, gap);
      if (plan) {
        for (const [id, d] of plan.moves) {
          const o = this.store.getObject(id)!;
          this.transient.set(id, { ...frameOf(o), x: o.x + d.x, y: o.y + d.y });
        }
        this.transient.set(section, { ...frameOf(this.store.getObject(section)!), w: plan.w, h: plan.h });
      }
    } else {
      const items = topLevel(g, this.unlocked(this.selection))
        .filter((id) => this.store.getObject(id)?.type !== 'connector' && g.world.has(id))
        .map((id) => ({ id, rect: aabb(g.world.get(id)!) }));
      const pos = arrangeLayout(items, mode, gap);
      for (const { id, rect } of items) {
        const o = this.store.getObject(id)!;
        const p = pos.get(id)!;
        this.transient.set(id, { ...frameOf(o), x: o.x + p.x - rect.minX, y: o.y + p.y - rect.minY });
      }
    }
    this.transientDirty = true;
    this.schedule();
  }

  cancelArrange(): void {
    this.dom?.world.removeAttribute('data-arranging');
    this.restore([...this.transient.keys()]);
  }

  applyArrange(mode: ArrangeMode, gap: number): void {
    this.dom?.world.removeAttribute('data-arranging');
    this.restore([...this.transient.keys()]);
    const section = this.arrangeSection();
    this.discrete(() => (section ? this.store.commands.arrangeSection(section, mode, gap) : this.store.commands.arrange(this.unlocked(this.selection), mode, gap)));
  }

  /**
   * A layout action is its own undo step, never merged with the edit just before it (capture timeout); nested calls join
   * the outer step.
   */
  private discrete(fn: () => void): void {
    if (this.discreteDepth++ === 0) this.store.beginGesture();
    try {
      fn();
    } finally {
      if (--this.discreteDepth === 0) this.store.endGesture();
    }
  }

  /** A world point or box in the coordinates of `o`'s parent (what its element is positioned in). */
  private toLocal<P extends Point>(o: Readonly<ObjectData>, p: P): P {
    const g = this.store.graph();
    const parent = g.parent.get(o.id) ?? null;
    const at = parent === null ? null : g.origin.get(parent);
    return at ? { ...p, x: p.x - at.x, y: p.y - at.y } : p;
  }

  /** World frame of an object drawn at local frame `f` (for the overlay). */
  private toWorld(id: string, f: Frame): Frame {
    const g = this.store.graph();
    const parent = g.parent.get(id) ?? null;
    const at = parent === null ? null : g.origin.get(parent);
    return at ? { ...f, x: f.x + at.x, y: f.y + at.y } : f;
  }

  /**
   * After a move or a creation: each object (not inside a group) lands in the open section under its centre, or on the
   * board. Writes inside the caller's undo step.
   */
  adoptIntoSections(ids: string[]): string[] {
    const g = this.store.graph();
    const moves = new Map<string | null, string[]>();
    for (const m of dropPlan(g, this.store.list(), ids, (id) => g.world.get(id) ?? null)) {
      if (m.to !== m.from) moves.set(m.to, [...(moves.get(m.to) ?? []), m.id]);
    }
    for (const [target, moved] of moves) this.store.commands.reparent(moved, target);
    return [...moves.keys()].filter((t): t is string => t !== null);
  }

  /** A gesture on a locked object: the padlock of the section that locks it (itself or its nearest locked container) shakes. */
  private shakeLock(id: string): void {
    const read = (x: string) => this.store.getObject(x);
    const lockId = read(id)?.locked ? id : ancestorsOf(this.store.graph(), id).find((a) => read(a)?.locked);
    const lock = lockId ? this.els.get(lockId)?.querySelector(`[data-section-lock="${lockId}"]`) : null;
    if (!lock) return;
    lock.setAttribute('data-shake', '');
    this.shakes.set(lock, this.clock.now() + SHAKE_MS);
    this.schedule();
  }

  /**
   * Objects this person just made fade in (CSS on data-born, none with reduced motion): never a burst of more than
   * BORN_MAX, never on a degraded render, never what a peer or an import adds (they do not come through here).
   */
  private born(ids: readonly string[]): void {
    if (!ids.length || ids.length > BORN_MAX || this.degraded) return;
    const until = this.clock.now() + BORN_MS;
    for (const id of ids) {
      this.bornUntil.set(id, until);
      this.els.get(id)?.setAttribute('data-born', '');
    }
    this.schedule();
  }

  /** The confirmation of a drop: the sections' outline pulses (CSS, none with reduced motion); the frame loop ends it. */
  private pulse(sections: string[]): void {
    const until = this.clock.now() + PULSE_MS;
    for (const id of sections) {
      this.els.get(id)?.setAttribute('data-drop-pulse', '');
      this.pulses.set(id, until);
    }
    if (sections.length) this.schedule();
  }

  /**
   * While a section is resized (P5e): its children that the new box would leave entirely outside (`reframeSection` lets
   * them go at the end) are marked `data-leaving`, with a label under the box. Null clears it.
   */
  private hintLeaving(id: string | null, box: Box | null): void {
    const g = this.store.graph();
    const next = new Set(
      id && box
        ? (g.children.get(id) ?? []).filter((c) => {
            const w = g.world.get(c);
            if (!w) return false;
            const r = obbBounds(obbOf(w));
            return r.maxX <= box.x || r.minX >= box.x + box.w || r.maxY <= box.y || r.minY >= box.y + box.h;
          })
        : [],
    );
    for (const c of this.leaving) if (!next.has(c)) this.els.get(c)?.removeAttribute('data-leaving');
    for (const c of next) this.els.get(c)?.setAttribute('data-leaving', '');
    const had = this.leaving.size > 0;
    this.leaving = next;
    if (next.size && id && box) {
      const s = this.store.getObject(id);
      const name = (s?.type === 'section' && s.name.trim()) || this.dropLabels.untitled;
      this.dropHint = { targets: [], label: { at: { x: box.x, y: box.y + box.h }, text: this.dropLabels.leaving(next.size, name) } };
      this.overlayDirty = true;
    } else if (had) {
      this.dropHint = null;
      this.overlayDirty = true;
    }
  }

  /** The drag highlight, from the live (transient) boxes: the same plan as `adoptIntoSections` at the commit. */
  private hintDrop(ids: string[]): void {
    const live = (id: string) => {
      const o = this.store.getObject(id);
      const f = this.transient.get(id) ?? (o && frameOf(o));
      return f ? this.toWorld(id, f) : null;
    };
    const plan = dropPlan(this.store.graph(), this.store.list(), ids, live);
    const name = (id: string) => {
      const s = this.store.getObject(id);
      return (s?.type === 'section' && s.name.trim()) || this.dropLabels.untitled;
    };
    const joining = plan.find((m) => m.to !== m.from && m.to !== null);
    const leaving = plan.find((m) => m.to !== m.from && m.from !== null);
    const text = joining ? this.dropLabels.into(name(joining.to!)) : leaving ? this.dropLabels.out(name(leaving.from!)) : null;
    const boxes = plan.map((m) => live(m.id)).filter((b): b is Frame => !!b);
    const at = boxes.length ? { x: Math.min(...boxes.map((b) => b.x)), y: Math.min(...boxes.map((b) => b.y)) } : null;
    this.dropHint = { targets: [...new Set(plan.map((m) => m.to).filter((t): t is string => t !== null))], label: text && at ? { at, text } : null };
    this.overlayDirty = true;
  }

  /** The exact final values (one undo step with the live writes); the DOM is then re-synced from the store. */
  private commit(ids: string[], write: () => void): void {
    if (!this.liveOpen) {
      this.store.beginGesture();
      this.liveOpen = true;
    }
    try {
      write();
    } finally {
      this.endLive(false);
      this.restore([...new Set([...ids, ...this.transient.keys()])]);
    }
  }

  /** Closes the live gesture; `revert` undoes its writes without leaving an undo or redo step (Escape). */
  private endLive(revert: boolean): void {
    if (this.liveOpen) {
      if (revert) this.store.cancelGesture();
      else this.store.endGesture();
    }
    this.liveOpen = false;
    this.live = { at: 0, dx: 0, dy: 0, deg: 0 };
  }

  private restore(ids: string[]): void {
    for (const id of ids) {
      const o = this.store.getObject(id);
      const el = this.els.get(id);
      if (o && el) writeFrame(el, frameOf(o));
    }
    this.transient.clear();
    this.origins.clear();
    this.fullCull = true;
    this.overlayDirty = true;
    this.schedule();
  }

  private clearHover(): void {
    if (this.hover !== NO_HOVER) {
      if (this.hover.grab && this.hover.id) this.els.get(this.hover.id)?.removeAttribute('data-section-hover');
      this.hover = NO_HOVER;
      this.overlayDirty = true;
      this.schedule();
    }
    this.hoverDirty = !!this.hoverInput;
    this.updateCursor();
  }

  private updateCursor(): void {
    const v = this.dom?.viewport;
    if (!v) return;
    const next = cursorFor({
      tool: this.toolId,
      hover: this.hover,
      selected: !!this.hover.id && this.selection.includes(this.hover.id),
      dragging: this.mode === 'pan',
      readOnly: this.store.readOnly,
    });
    if (next !== this.cursor) v.style.cursor = this.cursor = next;
  }

  private schedule(): void {
    if (this.dom && !this.frameHandle) this.frameHandle = this.clock.raf(this.tick);
  }

  private tick = (now: number): void => {
    this.frameHandle = 0;
    const dom = this.dom;
    if (!dom) return;
    if (this.busyNow) this.updateBusy();
    if (this.chained && !this.degraded) this.watchFrame(now - this.lastTick, now, dom);
    this.lastTick = now;
    if (this.cameraDirty) {
      const { x, y, z } = this.camera;
      dom.world.style.transform = `matrix(${z}, 0, 0, ${z}, ${-x * z}, ${-y * z})`;
      dom.world.style.setProperty('--inv-zoom', String(1 / z)); // constant-size labels (section titles)
      // Page links (P14) switch between a live miniature and a static card by their size on screen: told only when z changes.
      if (z !== this.zoomSent) {
        this.zoomSent = z;
        dom.world.dispatchEvent(new Event(ZOOM_EVENT));
      }
      const bg = this.store.meta().background;
      if (dom.background) Object.assign(dom.background.style, backgroundCss(bg, this.camera, this.store.meta().grid.size));
      const ink = canvasColors(bg);
      dom.world.style.setProperty('--canvas-ink', ink.ink);
      dom.world.style.setProperty('--canvas-fill', ink.fill);
      if (!this.willChangeOn) dom.world.style.willChange = 'transform';
      this.willChangeOn = true;
      this.cull(this.fullCull);
      this.cameraDirty = false;
      this.fullCull = false;
      this.overlayDirty = true;
    } else if (this.fullCull) {
      this.cull(true);
      this.fullCull = false;
    }
    if (this.transientDirty) {
      for (const [id, f] of this.transient) {
        const el = this.els.get(id);
        if (el) writeFrame(el, f);
      }
      this.transientDirty = false;
      this.overlayDirty = true;
    }
    if (this.hoverDirty) {
      this.hoverDirty = false;
      const input = this.hoverInput;
      if (input && this.pointers.size === 0 && !this.store.readOnly && this.tool?.onHover) this.apply(this.tool.onHover(input));
    }
    if (this.overlayDirty) {
      const model = this.overlayModel();
      if (model !== EMPTY_OVERLAY || !this.overlayEmpty) drawOverlay(dom.overlay, model);
      this.overlayEmpty = model === EMPTY_OVERLAY;
      this.overlayDirty = false;
    }
    if (this.shakes.size) {
      const t = this.clock.now();
      for (const [el, until] of this.shakes) {
        if (t < until) continue;
        el.removeAttribute('data-shake');
        this.shakes.delete(el);
      }
      if (this.shakes.size) this.schedule();
    }
    if (this.bornUntil.size) {
      const t = this.clock.now();
      for (const [id, until] of this.bornUntil) {
        if (t < until) continue;
        this.els.get(id)?.removeAttribute('data-born');
        this.bornUntil.delete(id);
      }
      if (this.bornUntil.size) this.schedule();
    }
    if (this.pulses.size) {
      const t = this.clock.now();
      for (const [id, until] of this.pulses) {
        if (t < until) continue;
        this.els.get(id)?.removeAttribute('data-drop-pulse');
        this.pulses.delete(id);
      }
      if (this.pulses.size) this.schedule();
    }
    const resting = this.pointers.size === 0 && now - this.lastActivity >= REST_MS;
    if (resting) {
      if (this.willChangeOn) dom.world.style.willChange = 'auto'; // the browser re-rasterizes text and images sharp
      this.willChangeOn = false;
      if (this.lodPending) this.swapLod();
      this.lodPending = false;
    } else if (this.willChangeOn || this.lodPending) {
      this.schedule();
    }
    this.chained = this.frameHandle !== 0; // only back-to-back frames measure frame time
  };

  /** Sustained slow frames: no shadows, LOD capped at 1024 for the rest of the session. */
  private watchFrame(delta: number, now: number, dom: EditorElements): void {
    if (delta <= SLOW_FRAME_MS) {
      this.slowSince = null;
      return;
    }
    this.slowSince ??= now - delta;
    if (now - this.slowSince < SLOW_FOR_MS) return;
    this.degraded = true;
    this.lodCap = Math.min(this.lodCap, 1024) as Variant;
    dom.world.dataset.degraded = 'true';
  }

  private viewportSize(): { w: number; h: number } {
    if (this.sizeOverride) return this.sizeOverride();
    const v = this.dom?.viewport;
    return { w: v?.clientWidth ?? 0, h: v?.clientHeight ?? 0 };
  }

  /** `clear`: keep the content out from under the bars (the commands; the camera of a board being opened stays as before). */
  private fitCamera(ids?: readonly string[], clear = false): Camera {
    const { w, h } = this.viewportSize();
    const g = this.store.graph();
    const objs = ids ? ids.map((id) => this.store.getObject(id)).filter((o) => !!o) : this.store.list();
    const bounds = unionRects(objs.filter((o) => !g.hidden.has(o.id)).map((o) => obbBounds(obbOf(g.world.get(o.id) ?? frameOf(o)))));
    if (!bounds || w <= 0 || h <= 0) return { x: -w / 2, y: -h / 2, z: 1 };
    return clear ? this.fitClearOfChrome(bounds, w, h) : fitRect(bounds, w, h);
  }

  private cull(full: boolean): void {
    if (!this.reader) return;
    const { w, h } = this.viewportSize();
    const next = new Set(this.reader.index().queryRect(visibleRect(this.camera, w, h, CULL_MARGIN)));
    for (const id of this.transient.keys()) next.add(id); // a dragged object never disappears mid-gesture
    // A container hides its whole subtree: keep the ancestors of anything visible displayed.
    const g = this.store.graph();
    for (const id of [...next]) for (const a of ancestorsOf(g, id)) next.add(a);
    if (full) {
      for (const [id, el] of this.els) el.style.display = next.has(id) ? '' : 'none';
    } else {
      for (const id of next) if (!this.visible.has(id)) this.els.get(id)?.style.setProperty('display', '');
      for (const id of this.visible) if (!next.has(id)) this.els.get(id)?.style.setProperty('display', 'none');
    }
    this.visible = next;

    const near = new Set(this.reader.index().queryRect(visibleRect(this.camera, w, h, RELEASE_MARGIN)));
    for (const id of this.transient.keys()) near.add(id);
    for (const id of full ? [...this.els.keys()] : [...this.near, ...near]) this.keepImage(id, near.has(id));
    this.near = near;
  }

  /** Far images give their decoded bitmap back; on return they get the thumbnail, then the right LOD at rest. */
  private keepImage(id: string, keep: boolean): void {
    const img = this.els.get(id)?.querySelector<HTMLImageElement>('img[data-asset]');
    if (!img?.dataset.asset) return;
    const loaded = img.hasAttribute('src');
    if (!keep && loaded) {
      img.removeAttribute('src');
      img.dataset.v = '';
      delete img.dataset.want;
    } else if (keep && !loaded) {
      const url = this.media.urlFor(img.dataset.asset, 256);
      if (!url) return; // not known yet: ImageObject sets it when it arrives
      img.src = url;
      img.dataset.v = '256';
      this.lodPending = true;
    }
  }

  private swapLod(): void {
    const dpr = this.dpr();
    for (const id of this.visible) {
      const img = this.els.get(id)?.querySelector<HTMLImageElement>('img[data-asset]');
      const o = this.store.getObject(id);
      if (!img || !o || !img.dataset.asset) continue;
      // A cropped image shows part of a larger source: pick the variant for the source's on-screen size.
      const scale = o.type === 'image' ? Math.max(o.w / o.crop.w, o.h / o.crop.h) : Math.max(o.w, o.h);
      const v = String(chooseVariant(scale * this.camera.z, dpr, this.lodCap));
      if (img.dataset.v === v || img.dataset.want === v) continue;
      const url = this.media.urlFor(img.dataset.asset, Number(v) as Variant);
      if (!url) continue; // asked again at the next rest, once the URL is known
      img.dataset.want = v;
      this.decode(url).then(
        () => {
          if (img.dataset.want !== v) return; // a newer swap superseded this one
          img.src = url;
          img.dataset.v = v;
        },
        () => undefined, // keep the current variant
      );
    }
  }

  private overlayModel(): OverlayModel {
    const cam = this.camera;
    const boxes: Point[][] = [];
    const dashed: Point[][] = [];
    const lines: Point[][] = [];
    const ends: Point[] = [];
    let mid: Point | null = null;
    let handles: OverlayModel['handles'] = [];
    let edge: OverlayModel['edge'] = null;
    let tint: string | null = null;
    for (const id of this.selection) {
      const o = this.store.getObject(id);
      const drawing = o?.type === 'connector' ? this.store.graph().paths.get(id) : undefined;
      if (drawing) {
        // A connector is outlined along its line; its two ends are its handles.
        lines.push(drawing.samples.map((p) => worldToScreen(cam, p)));
        if (this.selection.length === 1 && !this.isLocked(id) && !this.store.readOnly) {
          ends.push(worldToScreen(cam, drawing.start), worldToScreen(cam, drawing.end));
          mid = worldToScreen(cam, drawing.mid);
        }
        continue;
      }
      const local = this.transient.get(id) ?? (o && frameOf(o));
      if (!local) continue;
      const f = this.toWorld(id, local);
      const obb = obbOf(o?.type === 'group' ? (this.store.graph().world.get(id) ?? f) : f);
      // A section draws its own outline in its colour once selected (P5e); the overlay keeps its handles only.
      if (o?.type !== 'section') (o?.type === 'group' ? dashed : boxes).push(obbCorners(obb).map((p) => worldToScreen(cam, p)));
      // Groups have no handles; containers never rotate.
      if (this.selection.length === 1 && o && !this.isLocked(id) && o.type !== 'group' && !this.store.readOnly) {
        handles = handlesFor(o.type).map((h) => ({ id: h, ...worldToScreen(cam, handleWorldPos(obb, h, cam.z)) }));
        if (o.type === 'section') tint = sectionAccent(o.fill); // its handles match the outline it draws itself
        const side = EDGE_CORNERS[this.hover.handle ?? ''];
        if (side && this.hover.id === id && this.mode === 'idle') {
          const c = obbCorners(obb);
          edge = { side: this.hover.handle!, a: worldToScreen(cam, c[side[0]]!), b: worldToScreen(cam, c[side[1]]!) };
        }
      }
    }
    let marquee: OverlayModel['marquee'] = null;
    if (this.marquee) {
      const a = worldToScreen(cam, { x: this.marquee.minX, y: this.marquee.minY });
      const b = worldToScreen(cam, { x: this.marquee.maxX, y: this.marquee.maxY });
      marquee = { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
    }
    let ghost: OverlayModel['ghost'] = null;
    if (this.ghost) {
      const a = worldToScreen(cam, { x: this.ghost.rect.minX, y: this.ghost.rect.minY });
      const b = worldToScreen(cam, { x: this.ghost.rect.maxX, y: this.ghost.rect.maxY });
      ghost = { kind: this.ghost.tool === 'shape' ? this.shapeKind : 'box', x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
    }
    const cropGeo = this.cropGeometry();
    let crop: OverlayModel['crop'] = null;
    if (cropGeo) {
      const f = cropGeo.full;
      crop = {
        full: [{ x: f.x, y: f.y }, { x: f.x + f.w, y: f.y }, { x: f.x + f.w, y: f.y + f.h }, { x: f.x, y: f.y + f.h }].map((p) => worldToScreen(cam, p)),
        rect: obbCorners(cropGeo.rect).map((p) => worldToScreen(cam, p)),
      };
      handles = HANDLE_IDS.filter((h) => h !== 'rotate').map((h) => ({ id: h, ...worldToScreen(cam, handleWorldPos(cropGeo.rect, h, cam.z)) }));
      boxes.length = 0;
    }
    const preview = this.linkPreview?.map((p) => worldToScreen(cam, p)) ?? null;
    const guides = this.guides.length ? guideMarks(this.guides, cam) : [];
    const hover = this.hoverOutline();
    const dots = this.anchorDotMarks();
    const plus = this.plusMarks();
    let target: OverlayModel['target'] = null;
    let targetDot: OverlayModel['targetDot'] = null;
    const tw = this.linkTarget?.objectId ? this.store.graph().world.get(this.linkTarget.objectId) : undefined;
    if (this.linkTarget && tw) {
      target = obbCorners(obbOf(tw)).map((p) => worldToScreen(cam, p));
      targetDot = worldToScreen(cam, this.linkTarget);
    }
    let drop: OverlayModel['drop'] = null;
    if (this.dropHint) {
      const g = this.store.graph();
      const targets = this.dropHint.targets.flatMap((id) => {
        const w = g.world.get(id);
        return w ? [obbCorners(obbOf(w)).map((p) => worldToScreen(cam, p))] : [];
      });
      const label = this.dropHint.label;
      drop = { targets, label: label ? { at: worldToScreen(cam, label.at), text: label.text } : null };
    }
    return boxes.length || dashed.length || lines.length || preview || marquee || ghost || guides.length || crop || hover || dots.length || target || plus.length || drop
      ? { boxes, dashed, lines, ends, mid, preview, handles, edge, marquee, ghost, guides, crop, hover, dots, target, targetDot, plus, drop, tint }
      : EMPTY_OVERLAY;
  }

  /** Plus buttons around a single selected, unlocked sticky note (click one to add a neighbor). */
  private plusMarks(): NonNullable<OverlayModel['plus']> {
    if (this.store.readOnly || this.textEditing || this.mode !== 'idle' || this.toolId !== 'select' || this.selection.length !== 1) return [];
    const id = this.selection[0]!;
    const o = this.store.getObject(id);
    const w = this.store.graph().world.get(id);
    if (o?.type !== 'sticky' || this.isLocked(id) || !w) return [];
    return plusButtons(obbOf(w), this.camera.z).map((b) => ({ side: b.side, at: worldToScreen(this.camera, b.at), active: this.hover.plus === b.side }));
  }

  /** Anchor dots of the hovered object, else of the single selected one (touch has no hover); none while a gesture runs. */
  private anchorDotMarks(): NonNullable<OverlayModel['dots']> {
    if (this.store.readOnly || this.textEditing || this.mode !== 'idle' || this.toolId !== 'select') return [];
    const id = this.hover.id ?? (this.selection.length === 1 ? this.selection[0]! : null);
    const o = id ? this.store.getObject(id) : undefined;
    const w = id ? this.store.graph().world.get(id) : undefined;
    if (!o || !w || o.type === 'connector' || o.type === 'group') return [];
    return anchorDots(obbOf(w), this.camera.z).map((d) => ({ at: worldToScreen(this.camera, d.at), active: this.hover.id === id && this.hover.dot === d.side }));
  }

  /** The outline of the hovered object when it is not selected: along its line for a connector, its box otherwise. */
  private hoverOutline(): OverlayModel['hover'] {
    const id = this.hover.id;
    if (!id || this.selection.includes(id) || this.mode !== 'idle') return null;
    const o = this.store.getObject(id);
    if (!o) return null;
    const cam = this.camera;
    const drawing = o.type === 'connector' ? this.store.graph().paths.get(id) : undefined;
    if (drawing) return { points: drawing.samples.map((p) => worldToScreen(cam, p)), line: true };
    const w = this.store.graph().world.get(id);
    return w ? { points: obbCorners(obbOf(w)).map((p) => worldToScreen(cam, p)), line: false } : null;
  }

  private local(e: { clientX: number; clientY: number }): Point {
    const r = this.dom!.viewport.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  /** `pick`: a section's title or grip under the pointer (P5e); captured gestures target the viewport, so none then. */
  private input(e: PointerEvent, p: Point): PointerInput {
    const pick = e.target instanceof Element ? e.target.closest('[data-section-grab]')?.getAttribute('data-section-grab') : null;
    return { x: p.x, y: p.y, pointerType: asPointerType(e.pointerType), shift: e.shiftKey, alt: e.altKey, meta: e.metaKey || e.ctrlKey, buttons: e.buttons, ...(pick ? { pick } : {}) };
  }

  private onGestureStart = (e: Event) => {
    e.preventDefault(); // no page zoom
    this.gestureScale = 1;
  };

  private onGestureChange = (e: Event) => {
    e.preventDefault();
    const g = e as GestureEvent;
    const factor = g.scale / this.gestureScale;
    this.gestureScale = g.scale;
    if (Number.isFinite(factor) && factor > 0) this.setCamera(zoomAt(this.camera, this.local(g), factor));
  };

  private onDblClick = (e: MouseEvent) => {
    // A double tap was just handled from the pointer events: the browser's own double-click must not do it twice.
    if (this.clock.now() - this.doubleTapAt < 600) return;
    this.doubleClickAt(e);
  };

  private doubleClickAt(e: { clientX: number; clientY: number; target: EventTarget | null }): void {
    if (isEditable(e.target) || !this.reader) return;
    // Read from the point, not the event target: pointer capture retargets the double-click to the viewport.
    const title = document.elementsFromPoint?.(e.clientX, e.clientY).find((el) => el.hasAttribute('data-section-title'));
    const titled = title?.getAttribute('data-section-title');
    const titledSection = titled ? this.store.getObject(titled) : undefined;
    if (titled && titledSection?.type === 'section') {
      if (this.store.readOnly || this.isLocked(titled)) return;
      this.setSelection([titled]);
      this.handlers.onEditSectionTitle?.(titled);
      return;
    }
    const p = screenToWorld(this.camera, this.local(e));
    const id = hitTest(this.reader.index(), this.reader.objects(), p, 4, this.camera.z);
    if (id && this.store.getObject(id)?.type === 'connector') {
      if (this.store.readOnly) return;
      this.setSelection([id]);
      this.handlers.onEditConnectorLabel?.(id);
      return;
    }
    if (id && this.store.getObject(id)?.type === 'pagelink' && selectionTarget(this.reader.objects(), id, this.entered) === id) {
      this.setSelection([id]);
      this.handlers.onOpenPageLink?.(id);
      return;
    }
    if (id && this.store.getObject(id)?.type === 'embed' && selectionTarget(this.reader.objects(), id, this.entered) === id) {
      this.setSelection([id]);
      this.embeds.activate(id);
      return;
    }
    if (id && this.store.getObject(id)?.type === 'image' && selectionTarget(this.reader.objects(), id, this.entered) === id) {
      this.startCrop(id);
      return;
    }
    const table = id ? this.store.getObject(id) : undefined;
    if (table?.type === 'table' && selectionTarget(this.reader.objects(), table.id, this.entered) === table.id) {
      // Same as section titles: the cell comes from the point, and must belong to the table on top there.
      this.setSelection([table.id]);
      if (this.store.readOnly || this.isLocked(table.id)) return;
      const cell = document
        .elementsFromPoint?.(e.clientX, e.clientY)
        .map((el) => el.getAttribute('data-cell')?.split(':') ?? [])
        .find(([rowId, colId]) => table.rows.some((r) => r.id === rowId) && table.cols.some((c) => c.id === colId));
      if (cell) this.handlers.onEditTableCell?.(table.id, cell[0]!, cell[1]!);
      return;
    }
    const list = id ? this.store.getObject(id) : undefined;
    if (list?.type === 'tasklist' && selectionTarget(this.reader.objects(), list.id, this.entered) === list.id) {
      this.setSelection([list.id]);
      if (this.store.readOnly || this.isLocked(list.id)) return;
      const hit = document
        .elementsFromPoint?.(e.clientX, e.clientY)
        .map((el) => el.getAttribute('data-task'))
        .find((k) => k === 'title' || list.items.some((t) => t.id === k));
      if (hit) this.handlers.onEditTask?.(list.id, hit === 'title' ? 'title' : { itemId: hit });
      return;
    }
    if (id) {
      // A double-click on a grouped object enters its group and selects the next level down.
      const objs = this.reader.objects();
      const outer = selectionTarget(objs, id, this.entered);
      if (outer !== id && this.store.getObject(outer)?.type === 'group') {
        const child = selectionTarget(objs, id, outer);
        this.setSelection([child]);
        this.entered = outer;
        if (child !== id) return;
      }
    }
    const empty = !id && !hitTest(this.reader.index(), this.reader.objects(), p, 4, this.camera.z, true); // locked objects too
    if (empty && this.toolId === 'select' && !this.store.readOnly) {
      // A double-click on empty canvas adds a sticky note there, in edition.
      let made: string | null = null;
      this.discrete(() => {
        made = this.createAt('sticky', p);
      });
      if (made) {
        this.setSelection([made]);
        this.handlers.onEditText?.(made);
      }
      return;
    }
    const o = id ? this.store.getObject(id) : undefined;
    if (o?.type !== 'sticky' && o?.type !== 'text' && o?.type !== 'shape') return;
    this.setSelection([o.id]);
    this.handlers.onEditText?.(o.id);
  }

  private longPress: { timer: ReturnType<typeof setTimeout>; at: Point } | null = null;

  private cancelLongPress(): void {
    if (this.longPress) clearTimeout(this.longPress.timer);
    this.longPress = null;
  }

  /** Selects what is under the point (unless it is already selected) and asks the UI for the context menu. */
  private openContextMenu(clientX: number, clientY: number, target: EventTarget | null): void {
    if (!this.reader || this.cropping) return;
    const p = this.worldAt(clientX, clientY);
    // A section's title or grip sits outside its box (P5e): the element under the point says which section it is (the
    // event's own target is the viewport once the right button's press captured the pointer).
    const grab = (target instanceof Element ? target.closest('[data-section-grab]') : null) ?? document.elementsFromPoint?.(clientX, clientY).find((el) => el.hasAttribute('data-section-grab'));
    const picked = grab?.getAttribute('data-section-grab');
    const hit = picked ?? hitTest(this.reader.index(), this.reader.objects(), p, 4, this.camera.z, true);
    const id = hit ? selectionTarget(this.reader.objects(), hit, this.entered) : null;
    if (!id) this.setSelection([]);
    else if (!this.selection.includes(id)) this.setSelection([id]);
    const el = target instanceof Element ? target.closest('[data-cell]') : null;
    const [rowId, colId] = el?.getAttribute('data-cell')?.split(':') ?? [];
    const one = id ? this.store.getObject(id) : undefined;
    const taskEl = target instanceof Element ? target.closest('[data-task]') : null;
    const taskId = taskEl?.getAttribute('data-task');
    const task = one?.type === 'tasklist' && one.items.some((t) => t.id === taskId) ? taskId! : null;
    this.handlers.onContextMenu?.({ x: clientX, y: clientY, cell: rowId && colId ? { rowId, colId } : null, task });
  }

  private onContextMenu = (e: MouseEvent) => {
    if (isEditable(e.target)) return;
    e.preventDefault();
    this.openContextMenu(e.clientX, e.clientY, e.target);
  };

  private onResize = () => {
    // The free area around the selection changes: its context bar hides and is placed again once settled (P5f).
    this.cameraMovedAt = this.clock.now();
    this.updateBusy();
    this.cameraDirty = true;
    this.schedule();
  };

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const p = this.local(e);
    const w = wheelToIntent({ deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, ctrlKey: e.ctrlKey, x: p.x, y: p.y });
    this.setCamera(w.kind === 'zoom' ? zoomAt(this.camera, w.at, w.factor) : panBy(this.camera, w.dx, w.dy));
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (isEditable(e.target)) return;
    if (this.cropping) {
      // Crop mode owns the keyboard: Enter applies, Escape cancels.
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        this.endCrop(e.key === 'Enter');
      }
      return;
    }
    const k: KeyInput = { key: e.key, code: e.code, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey, ctrl: e.ctrlKey };
    const combo = comboOf(k);
    if (combo === 'o') this.shapeKind = 'ellipse';
    else if (combo === 'r') this.shapeKind = 'rectangle';
    if (e.key === '?') {
      e.preventDefault();
      this.handlers.onHelp?.();
      return;
    }
    // Tab on a single selected sticky note adds one to its right; otherwise Tab moves the focus as usual. A note that Tab
    // itself reached is walked past, or a keyboard user could never Tab out of a board of notes (WCAG 2.1.2, P12a).
    const one = this.selection.length === 1 ? this.store.getObject(this.selection[0]!) : undefined;
    if (e.key === 'Tab' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && one?.type === 'sticky' && one.id !== this.tabbedTo && !this.store.readOnly) {
      e.preventDefault();
      this.createNeighbor(one.id, 'right');
      return;
    }
    if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (this.focusNext(e.shiftKey ? -1 : 1)) e.preventDefault();
      return;
    }
    if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
      e.preventDefault();
      this.openMenuAtSelection();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && one?.type === 'pagelink' && this.toolId === 'select') {
      // Opening a page is reading, not editing: readers open links too (P14).
      e.preventDefault();
      this.handlers.onOpenPageLink?.(one.id);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && !this.store.readOnly) {
      if (CREATE_TOOLS.has(this.toolId)) {
        // Keyboard creation: the armed tool places its object at the view's centre, as a click there would.
        e.preventDefault();
        const created = this.toolId;
        let id: string | null = null;
        this.discrete(() => {
          id = this.createAt(created, this.viewCenter());
        });
        if (id) this.setSelection([id]);
        this.setTool('select');
        this.editCreated(created, id);
        return;
      }
      if (one && !this.isLocked(one.id) && (one.type === 'sticky' || one.type === 'text' || one.type === 'shape')) {
        e.preventDefault();
        this.handlers.onEditText?.(one.id);
        return;
      }
      if (one?.type === 'section' && !this.isLocked(one.id)) {
        e.preventDefault();
        this.handlers.onEditSectionTitle?.(one.id);
        return;
      }
      if (one?.type === 'table' && !this.isLocked(one.id) && one.rows[0] && one.cols[0]) {
        e.preventDefault();
        this.handlers.onEditTableCell?.(one.id, one.rows[0].id, one.cols[0].id);
        return;
      }
      if (one?.type === 'tasklist' && !this.isLocked(one.id)) {
        e.preventDefault();
        this.handlers.onEditTask?.(one.id, one.items[0] ? { itemId: one.items[0].id } : 'title');
        return;
      }
    }
    if (e.key === 'Escape' && this.toolId !== 'select') {
      // A box being dragged out is dropped with its preview (P5f); the release then creates nothing.
      if (this.mode === 'tool' && this.tool) this.apply(this.tool.onCancel());
      this.setTool('select');
      this.settleTool();
      return;
    }
    const action = lookupShortcut(k);
    if (action) {
      e.preventDefault();
      if (isTool(action)) this.pickTool(action);
      else this.runCommand(action);
      return;
    }
    const intents = this.tool?.onKey(k) ?? [];
    if (intents.length) {
      e.preventDefault();
      this.apply(intents);
    }
  };

  private onDown = (e: PointerEvent) => {
    this.down(e);
    this.updateBusy();
  };

  private down(e: PointerEvent): void {
    if (isEditable(e.target)) return;
    const p = this.local(e);
    // A first finger near a side edge is the system's back gesture: no focus, no capture, no tool.
    if (e.pointerType === 'touch' && this.pointers.size === 0 && (p.x < EDGE_GUTTER_PX || p.x > this.viewportSize().w - EDGE_GUTTER_PX)) return;
    this.focus();
    this.pointers.set(e.pointerId, p);
    try {
      this.dom?.viewport.setPointerCapture?.(e.pointerId);
    } catch {
      // capture is a nicety (the pointer may already be gone)
    }
    if (e.pointerType === 'touch') this.pinch.down(e.pointerId, p);
    if (this.pointers.size === 2 && e.pointerType === 'touch') {
      // A second finger turns whatever the first one started into a pinch.
      this.cancelLongPress();
      this.tapDown = null;
      if (this.mode === 'tool' && this.tool) this.apply(this.tool.onCancel());
      this.mode = 'pinch';
      return;
    }
    if (this.pointers.size > 1) return;
    this.primary = e.pointerId;
    if (this.cropping) {
      const geo = this.cropGeometry();
      const handle = geo && handleAt(geo.rect, screenToWorld(this.camera, p), this.camera.z, asPointerType(e.pointerType));
      if (handle && handle !== 'rotate') {
        this.cropHandle = handle;
        this.mode = 'crop';
        return;
      }
      const w = screenToWorld(this.camera, p);
      const f = geo?.full;
      if (f && w.x >= f.x && w.x <= f.x + f.w && w.y >= f.y && w.y <= f.y + f.h) return; // inside the image: stay
      this.endCrop(true); // a click elsewhere applies the crop, then acts as usual
    }
    if (e.pointerType === 'touch') this.tapDown = { id: e.pointerId, p, moved: false };
    if (this.toolId === 'hand' || (e.buttons & 4) !== 0) {
      this.mode = 'pan';
      this.updateCursor();
      if (e.pointerType === 'touch' && this.toolId === 'hand') this.armPickUp(e, p);
      return;
    }
    if (this.toolId === 'comment') {
      this.mode = 'comment';
      this.commentDown = p;
      return;
    }
    if (CLICK_TOOLS.has(this.toolId)) {
      const created = this.toolId;
      const id = this.createAt(created, screenToWorld(this.camera, p));
      if (id) this.setSelection([id]);
      this.setTool('select');
      this.editCreated(created, id, true);
      this.mode = 'idle';
      return;
    }
    this.mode = 'tool';
    const input = this.input(e, p);
    if (this.tool) this.apply(this.tool.onPointerDown(this.multiSelect ? { ...input, shift: true } : input));
    // A finger held still for 500 ms opens the context menu (touch has no right click).
    if (e.pointerType === 'touch' && this.toolId === 'select') {
      const { clientX, clientY, target } = e;
      this.cancelLongPress();
      this.longPress = {
        at: p,
        timer: setTimeout(() => {
          this.longPress = null;
          this.tapDown = null;
          if (this.mode === 'tool' && this.tool) this.apply(this.tool.onCancel());
          this.mode = 'idle';
          this.openContextMenu(clientX, clientY, target);
        }, LONG_PRESS_MS),
      };
    }
  }

  private onMove = (e: PointerEvent) => {
    this.pointerWorld = this.worldAt(e.clientX, e.clientY);
    if (this.pointerSubs.size) {
      const at = this.pointerWorld;
      this.pointerSubs.forEach((cb) => cb(at));
    }
    const prev = this.pointers.get(e.pointerId);
    if (!prev) {
      if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
        this.hoverInput = this.input(e, this.local(e));
        this.hoverDirty = true;
        this.schedule();
      }
      return;
    }
    const p = this.local(e);
    this.pointers.set(e.pointerId, p);
    if (this.longPress && Math.hypot(p.x - this.longPress.at.x, p.y - this.longPress.at.y) > 6) this.cancelLongPress();
    if (this.tapDown?.id === e.pointerId && !this.tapDown.moved) {
      const d = this.tapDown.p;
      this.tapDown.moved = Math.hypot(p.x - d.x, p.y - d.y) > TAP_SLOP_PX;
    }
    const update = e.pointerType === 'touch' ? this.pinch.move(e.pointerId, p) : null;
    if (this.mode === 'pinch') {
      if (update) this.setCamera(panBy(zoomAt(this.camera, update.at, update.zoomFactor), update.pan.x, update.pan.y));
      return;
    }
    if (e.pointerId !== this.primary) return;
    if (this.mode === 'crop') {
      const geo = this.cropGeometry();
      if (geo && this.cropHandle && this.cropDraft) {
        this.cropDraft = cropFrom(this.cropDraft, this.cropHandle, screenToWorld(this.camera, p), geo.full);
        this.overlayDirty = true;
        this.schedule();
      }
      return;
    }
    if (this.mode === 'pan') this.setCamera(panBy(this.camera, p.x - prev.x, p.y - prev.y));
    else if (this.mode === 'tool' && this.tool) this.apply(this.tool.onPointerMove(this.input(e, p)));
  };

  private onLeave = () => {
    this.hoverInput = null;
    this.clearHover();
    this.pointerWorld = null;
    this.pointerSubs.forEach((cb) => cb(null));
  };

  private onUp = (e: PointerEvent) => {
    this.up(e);
    this.updateBusy();
  };

  private up(e: PointerEvent): void {
    if (!this.pointers.has(e.pointerId)) return;
    this.cancelLongPress();
    const p = this.local(e);
    this.pointers.delete(e.pointerId);
    this.pinch.up(e.pointerId);
    this.lastActivity = this.clock.now();
    const tapped = this.tapDown?.id === e.pointerId && !this.tapDown.moved && e.type !== 'pointercancel' && this.mode !== 'pinch';
    if (this.tapDown?.id === e.pointerId) this.tapDown = null;
    if (this.mode === 'tool' && e.pointerId === this.primary && this.tool) {
      this.apply(e.type === 'pointercancel' ? this.tool.onCancel() : this.tool.onPointerUp(this.input(e, p)));
    }
    if (tapped) this.onTap(e, p);
    if (this.mode === 'comment' && e.pointerId === this.primary && e.type !== 'pointercancel' && this.reader && this.commentDown) {
      // A click, not a drag: the pin goes on the topmost object under the pointer, locked ones included.
      if (Math.hypot(p.x - this.commentDown.x, p.y - this.commentDown.y) <= COMMENT_CLICK_PX) {
        const at = screenToWorld(this.camera, p);
        const objectId = hitTest(this.reader.index(), this.reader.objects(), at, 4, this.camera.z, true, Infinity); // a comment inside a section still follows it
        this.handlers.onComment?.({ at, objectId, screen: p });
      }
      this.commentDown = null;
    }
    if (this.pointers.size === 0) {
      this.mode = 'idle';
      this.primary = null;
      this.cropHandle = null;
      if (this.hoverInput && e.pointerType !== 'touch') {
        this.hoverInput = this.input(e, p);
        this.hoverDirty = true;
      }
      this.updateCursor();
      this.settleTool();
      this.flushReveal();
      this.emitUi();
    }
    this.schedule();
  }

  /** Mobile, hand tool: a finger held still on an object picks it up; on empty space it opens the canvas menu. */
  private armPickUp(e: PointerEvent, p: Point): void {
    const { clientX, clientY, target } = e;
    this.cancelLongPress();
    this.longPress = {
      at: p,
      timer: setTimeout(() => {
        this.longPress = null;
        this.tapDown = null;
        const id = this.objectAt(clientX, clientY, target);
        if (!id) {
          this.mode = 'idle';
          this.openContextMenu(clientX, clientY, target);
          return;
        }
        if (typeof navigator !== 'undefined') navigator.vibrate?.(12);
        this.setSelection([id]);
        this.setTool('select');
        // A task of a list or a cell of a table has its own menu: that is what holding it is for.
        if (target instanceof Element && target.closest('[data-task],[data-cell]')) {
          this.mode = 'idle';
          this.openContextMenu(clientX, clientY, target);
          return;
        }
        // The finger is still down: it now drags what it has just picked up.
        this.mode = 'tool';
        if (this.tool) this.apply(this.tool.onPointerDown(this.input(e, p)));
        this.updateBusy();
      }, LONG_PRESS_MS),
    };
  }

  /** The topmost object under a client point (locked ones too), as the selection would take it. */
  private objectAt(clientX: number, clientY: number, target: EventTarget | null): string | null {
    if (!this.reader) return null;
    const grab = (target instanceof Element ? target.closest('[data-section-grab]') : null) ?? document.elementsFromPoint?.(clientX, clientY).find((el) => el.hasAttribute('data-section-grab'));
    const picked = grab?.getAttribute('data-section-grab');
    const hit = picked ?? hitTest(this.reader.index(), this.reader.objects(), this.worldAt(clientX, clientY), 10, this.camera.z, true);
    return hit ? selectionTarget(this.reader.objects(), hit, this.entered) : null;
  }

  /** A short still touch: the second of two close ones is a double tap (the browser's own double-click does not reach a finger reliably). */
  private onTap(e: PointerEvent, p: Point): void {
    const now = this.clock.now();
    const prev = this.lastTap;
    const double = !!prev && now - prev.at <= DOUBLE_TAP_MS && Math.hypot(p.x - prev.p.x, p.y - prev.p.y) <= DOUBLE_TAP_SLOP_PX;
    this.lastTap = double ? null : { at: now, p };
    if (!double) return;
    this.doubleTapAt = now;
    if (this.toolId === 'hand') {
      const id = this.objectAt(e.clientX, e.clientY, e.target);
      if (!id) return;
      this.setSelection([id]);
      this.setTool('select');
      return;
    }
    this.doubleClickAt(e);
  }
}

/** Snapping guides as screen segments with an optional label (distances in world px, rounded). */
function guideMarks(guides: Guide[], cam: Camera): NonNullable<OverlayModel['guides']> {
  const out: NonNullable<OverlayModel['guides']> = [];
  const seg = (a: Point, b: Point, label?: string) => out.push({ a: worldToScreen(cam, a), b: worldToScreen(cam, b), label });
  for (const g of guides) {
    if (g.kind === 'align') {
      if (g.axis === 'x') seg({ x: g.at, y: g.from }, { x: g.at, y: g.to });
      else seg({ x: g.from, y: g.at }, { x: g.to, y: g.at });
    } else if (g.kind === 'gap') {
      for (const s of g.segments) {
        const label = String(Math.round(g.value));
        if (g.axis === 'x') seg({ x: s.from, y: s.at }, { x: s.to, y: s.at }, label);
        else seg({ x: s.at, y: s.from }, { x: s.at, y: s.to }, label);
      }
    } else {
      for (const r of g.rects) {
        const label = String(Math.round(g.value));
        if (g.dim === 'w') seg({ x: r.minX, y: r.maxY + 8 / cam.z }, { x: r.maxX, y: r.maxY + 8 / cam.z }, label);
        else seg({ x: r.maxX + 8 / cam.z, y: r.minY }, { x: r.maxX + 8 / cam.z, y: r.maxY }, label);
      }
    }
  }
  return out;
}

function rotateFrame(f: Frame, deltaDeg: number, pivot: Point): Frame {
  const r = (deltaDeg * Math.PI) / 180;
  const cx = f.x + f.w / 2 - pivot.x;
  const cy = f.y + f.h / 2 - pivot.y;
  const nx = pivot.x + cx * Math.cos(r) - cy * Math.sin(r);
  const ny = pivot.y + cx * Math.sin(r) + cy * Math.cos(r);
  return { x: nx - f.w / 2, y: ny - f.h / 2, w: f.w, h: f.h, rotation: normalizeAngle(f.rotation + deltaDeg) };
}
